import React, { useEffect, useState, useRef, useCallback, useMemo, memo } from 'react';
import { whatsappAPI } from './whatsappAPI';
import { Link, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Send, Search, MessageCircle, MessageSquarePlus, Trash2, Check, CheckCheck, Clock,
  AlertCircle, RotateCw, ChevronDown, User, UserCheck, ArrowRightLeft, Archive, ArchiveRestore,
  CheckCircle2, MoreVertical, StickyNote, Bot, ClipboardList, X, Loader2, Lock,
  Tag, SlidersHorizontal, Layers, Plus, PanelRight, Zap, SquareKanban, AtSign, Bell, Sparkles, Frown, Angry, Users, Download, Paperclip, UploadCloud, Mic,
} from 'lucide-react';
import NovaMensagemModal from './NovaMensagemModal';
import { AnexosModal, BarraGravacao, BotaoMic, useGravador, aceitarArquivos, tipoDe, tempo as fmtTempo } from './AnexosEnvio';
import { useAuth } from '../../context/AuthContext';
import { formatWa, semMarcas } from './waFormat';
import { useOrganizacao, SITUACOES, situacaoInfo, Chip, useCfgAtendimento, telefoneConversa, ehLid } from './organizacao';
import PainelCliente, { fmtProtocolo } from './PainelCliente';
import BuscaGlobal from './BuscaGlobal';
import RespostasRapidas, { useRespostas, filtrarRespostas } from './RespostasRapidas';
import KanbanAtendimento from './KanbanAtendimento';
import { useWaRealtime, setConversaAberta } from './waRealtime';

/* ============================================================================
 * Atendimento WhatsApp — v2
 * - Tempo real pelo WebSocket já existente (/api/chat/ws, eventos wa_*); poll só como reserva
 * - Poll com intervalo fixo (a v1 tinha [conversations] nas deps → requisições em loop contínuo)
 * - Mensagens incrementais (?since=), sem recarregar/re-renderizar tudo a cada 4s
 * - Rolagem inteligente: não puxa para baixo enquanto o atendente lê o histórico
 * - Envio otimista (aparece na hora) + falha visível com "tentar de novo"
 * - Banner de conexão (reconectando / desconectado)
 * ========================================================================== */

const POLL_CONVS_MS_WS = 30000;   // com WebSocket conectado
const POLL_CONVS_MS = 6000;       // sem WebSocket
const POLL_MSGS_MS = 4000;        // sem WebSocket
const POLL_MSGS_MS_WS = 20000;    // com WebSocket: só uma checagem de segurança (busca apenas o que for novo)
const AVATAR_COLORS = ['#0f766e', '#b45309', '#4338ca', '#be185d', '#15803d', '#0369a1', '#7c3aed', '#c2410c'];
const SENDER_COLORS = ['#7c3aed', '#0369a1', '#b45309', '#be185d', '#15803d', '#c2410c'];

const hash = (s = '') => [...s].reduce((a, c) => (a * 31 + c.charCodeAt(0)) >>> 0, 7);
const colorFor = (s, pal) => pal[hash(s) % pal.length];
const initials = (n = '?') => n.replace(/[^\p{L}\s]/gu, '').trim().split(/\s+/).slice(0, 2).map(w => w[0]).join('').toUpperCase() || '?';
const ABAS = [['meus', 'Meus'], ['fila', 'Fila'], ['ativas', 'Todas'], ['grupos', 'Grupos'], ['cadastros', 'Cadastros'], ['arquivadas', 'Encerradas']];
const minutosDesde = (iso) => (iso ? Math.max(0, Math.round((Date.now() - Date.parse(iso)) / 60000)) : 0);
const fmtEspera = (iso) => { const m = minutosDesde(iso); return m < 60 ? `${m} min` : `${Math.floor(m / 60)}h${String(m % 60).padStart(2, '0')}`; };
const errMsg = (e, padrao) => e?.response?.data?.detail || padrao;
const displayName = (c) => c?.cliente_nome || c?.push_name || telefoneConversa(c) || (c?.is_grupo ? 'Grupo do WhatsApp' : ehLid(c) ? 'Contato do WhatsApp' : 'Sem nome');
const lerLS = (k, padrao) => { try { const v = localStorage.getItem(k); return v === null ? padrao : v === '1'; } catch (_) { return padrao; } };
const gravarLS = (k, v) => { try { localStorage.setItem(k, v ? '1' : '0'); } catch (_) { /* noop */ } };
function useLargo(query = '(min-width: 1536px)') {      // tela larga: ficha do contato vira 3ª coluna
  const [ok, setOk] = useState(() => typeof window.matchMedia === 'function' && window.matchMedia(query).matches);
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return undefined;
    const mq = window.matchMedia(query); const fn = () => setOk(mq.matches);
    mq.addEventListener ? mq.addEventListener('change', fn) : mq.addListener(fn);
    return () => (mq.removeEventListener ? mq.removeEventListener('change', fn) : mq.removeListener(fn));
  }, [query]);
  return ok;
}
// "ring" do avatar indica o estado da conversa
const anelEstado = (c, userId) => {
  const st = c?.atendimento_status || 'bot';
  if (st === 'fila') return '#f59e0b';
  if (st === 'em_atendimento') return c.atendente_id === userId ? '#059669' : '#38bdf8';
  if (st === 'bot') return '#a78bfa';
  return null;
};

function fmtListTime(iso) {
  if (!iso) return '';
  const d = new Date(iso); const now = new Date();
  const days = Math.floor((new Date(now.toDateString()) - new Date(d.toDateString())) / 864e5);
  if (days === 0) return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  if (days === 1) return 'Ontem';
  if (days < 7) return d.toLocaleDateString('pt-BR', { weekday: 'short' }).replace('.', '');
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
}
function fmtDay(iso) {
  const d = new Date(iso); const now = new Date();
  const days = Math.floor((new Date(now.toDateString()) - new Date(d.toDateString())) / 864e5);
  if (days === 0) return 'Hoje';
  if (days === 1) return 'Ontem';
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: days > 300 ? 'numeric' : undefined });
}

/* Som e aviso de mensagem nova, transferência e menção ficam no sino do topo (AlertasWA), em todas as telas. */

/* ============================================================================ */

export default function Atendimento() {
  const [conversations, setConversations] = useState([]);
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const [text, setText] = useState('');
  const [search, setSearch] = useState('');
  const [aba, setAba] = useState('ativas');
  const [contadores, setContadores] = useState({});
  const [modoNota, setModoNota] = useState(false);
  const [showFinalizar, setShowFinalizar] = useState(false);
  const [anexos, setAnexos] = useState([]);          // arquivos escolhidos, esperando a prévia/legenda
  const [arrastando, setArrastando] = useState(false);
  const arquivoInput = useRef(null);
  const bloqueadoRef = useRef(false);
  const envios = useRef({});                         // tempId → { arquivo, legenda, voz } para "tentar de novo"
  const [showTransfer, setShowTransfer] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [acao, setAcao] = useState(null);
  const { user, isAdmin } = useAuth();
  const adminSistema = typeof isAdmin === 'function' ? isAdmin() : !!isAdmin;
  const [perfil, setPerfil] = useState(adminSistema ? 'administrador' : 'atendente');
  const [respondeTodas, setRespondeTodas] = useState(adminSistema);
  useEffect(() => { whatsappAPI.eu().then(r => { if (r.data?.perfil) setPerfil(r.data.perfil); setRespondeTodas(!!r.data?.responde_todas); }).catch(() => {}); }, []);
  const admin = adminSistema || perfil === 'supervisor';   // "admin" aqui = pode supervisionar conversas
  const cfgAtend = useCfgAtendimento();
  const operador = perfil === 'operador';
  const [mencoes, setMencoes] = useState({ itens: [], nao_lidas: 0 });
  const [showMencoes, setShowMencoes] = useState(false);
  const carregarMencoes = useCallback(() => whatsappAPI.mencoes().then(r => setMencoes(Array.isArray(r.data?.itens) ? r.data : { itens: [], nao_lidas: 0 })).catch(() => {}), []);
  useEffect(() => { carregarMencoes(); }, [carregarMencoes]);
  const [equipeLista, setEquipeLista] = useState([]);
  const [ia, setIa] = useState(null);
  useEffect(() => { whatsappAPI.iaStatus().then(r => setIa(r.data)).catch(() => {}); }, []);
  const [sug, setSug] = useState(null);                 // null | 'carregando' | [textos]
  const [arroba, setArroba] = useState(null);          // { termo, idx } enquanto digita @ numa nota
  const mencoesSel = useRef([]);
  const [showNova, setShowNova] = useState(false);
  const [waStatus, setWaStatus] = useState(null);
  const [atBottom, setAtBottom] = useState(true);
  const [novasAbaixo, setNovasAbaixo] = useState(0);
  const [filtros, setFiltros] = useState({ setor: '', etiqueta: '', situacao: '', etapa: '' });
  const [showPainel, setShowPainel] = useState(() => lerLS('wa_painel', true));
  const largo = useLargo();
  const [painelTick, setPainelTick] = useState(0);
  const [quadroTick, setQuadroTick] = useState(0);
  const [modo, setModoState] = useState(() => { try { return localStorage.getItem('wa_modo') === 'quadro' ? 'quadro' : 'lista'; } catch (_) { return 'lista'; } });
  const setModo = (m) => { setModoState(m); try { localStorage.setItem('wa_modo', m); } catch (_) { /* noop */ } };
  const [params, setParams] = useSearchParams();
  const [destaqueMsg, setDestaqueMsg] = useState(null);
  const pularParaRef = useRef(null);
  const [resp, setResp] = useState({ aberto: false, filtro: '', idx: 0 });
  const respostas = useRespostas(resp.aberto);
  const respItens = useMemo(() => filtrarRespostas(respostas, resp.filtro), [respostas, resp.filtro]);
  // tela larga: ficha fixa (preferência lembrada); notebook/tablet/celular: abre por cima só quando pedir
  const [painelSobre, setPainelSobre] = useState(false);
  const painelAberto = largo ? showPainel : painelSobre;
  const alternarPainel = () => {
    if (largo) setShowPainel(v => { gravarLS('wa_painel', !v); return !v; });
    else setPainelSobre(v => !v);
  };
  const [showFiltros, setShowFiltros] = useState(false);
  const { setores, etiquetas } = useOrganizacao();
  const setoresMap = useMemo(() => Object.fromEntries(setores.map(x => [x.id, x])), [setores]);
  const etiquetasMap = useMemo(() => Object.fromEntries(etiquetas.map(x => [x.id, x])), [etiquetas]);

  const convsRef = useRef([]);
  const selectedRef = useRef(null);
  const messagesRef = useRef([]);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const sendingRef = useRef(false);
  const atBottomRef = useRef(true);
  const abaRef = useRef(aba);
  const filtrosRef = useRef(filtros);
  const ultimaAbaCarregada = useRef(null);
  abaRef.current = aba;
  filtrosRef.current = filtros;
  selectedRef.current = selected;
  messagesRef.current = messages;
  atBottomRef.current = atBottom;

  /* ---------- conversas ---------- */
  const loadConversations = useCallback(async () => {
    try {
      const abaAtual = `${abaRef.current}|${JSON.stringify(filtrosRef.current)}`;
      const [{ data }, cont] = await Promise.all([
        whatsappAPI.conversations(abaRef.current, filtrosRef.current),
        whatsappAPI.contadores().catch(() => null),
      ]);
      if (abaAtual !== `${abaRef.current}|${JSON.stringify(filtrosRef.current)}`) return;   // trocou de aba/filtro no meio
      if (cont?.data) setContadores(cont.data);
      const novos = data || [];
      ultimaAbaCarregada.current = abaAtual;
      convsRef.current = novos;
      setConversations(novos);
      // mantém o cabeçalho da conversa aberta atualizado (status, atendente...)
      setSelected(sel => { const f = sel && novos.find(c => c.jid === sel.jid); return f ? { ...sel, ...f } : sel; });
    } catch (_) { /* noop */ }
  }, []);

  const loadStatus = useCallback(async () => {
    try { const { data } = await whatsappAPI.sessionStatus(); setWaStatus(data?.status || 'offline'); }
    catch (_) { setWaStatus('service_offline'); }
  }, []);

  /* ---------- rolagem ---------- */
  const scrollToBottom = useCallback((smooth = false) => {
    const el = scrollRef.current; if (!el) return;
    requestAnimationFrame(() => el.scrollTo({ top: el.scrollHeight, behavior: smooth ? 'smooth' : 'auto' }));
    setNovasAbaixo(0);
  }, []);
  const onScroll = () => {
    const el = scrollRef.current; if (!el) return;
    const bottom = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
    if (bottom !== atBottomRef.current) setAtBottom(bottom);
    if (bottom) setNovasAbaixo(0);
  };

  /* ---------- mensagens ---------- */
  const mergeMessages = useCallback((incoming) => {
    if (!incoming.length) return;
    setMessages(prev => {
      const byId = new Map(prev.map(m => [m.id, m]));
      let added = 0;
      for (const m of incoming) {
        if (byId.has(m.id)) byId.set(m.id, { ...byId.get(m.id), ...m });
        else { byId.set(m.id, m); added += !m.from_me; }
      }
      if (added && !atBottomRef.current) setNovasAbaixo(n => n + added);
      return [...byId.values()].sort((a, b) => String(a.timestamp).localeCompare(String(b.timestamp)));
    });
    if (atBottomRef.current) scrollToBottom(true);
  }, [scrollToBottom]);

  const loadMessages = useCallback(async (jid) => {
    setLoadingMsgs(true);
    try {
      const alvo = pularParaRef.current;
      const { data } = await whatsappAPI.messages(jid, null, alvo ? 500 : undefined);
      if (selectedRef.current?.jid !== jid) return;
      setMessages(data || []);
      if (alvo && (data || []).some(m => m.id === alvo)) {          // veio da busca: vai até a mensagem
        pularParaRef.current = null;
        setAtBottom(false);
        requestAnimationFrame(() => requestAnimationFrame(() => {
          document.querySelector(`[data-testid="wa-msg-${CSS.escape(alvo)}"]`)?.scrollIntoView({ block: 'center' });
        }));
        setDestaqueMsg(alvo); setTimeout(() => setDestaqueMsg(d => (d === alvo ? null : d)), 2600);
      } else {
        pularParaRef.current = null;
        setAtBottom(true);
        scrollToBottom(false);
      }
      setConversations(cs => cs.map(c => (c.jid === jid ? { ...c, nao_lidas: 0 } : c)));
    } catch (_) { toast.error('Não foi possível carregar a conversa'); }
    finally { setLoadingMsgs(false); }
  }, [scrollToBottom]);

  const pollNewMessages = useCallback(async () => {
    const jid = selectedRef.current?.jid; if (!jid) return;
    const last = [...messagesRef.current].reverse().find(m => !m._temp)?.timestamp;
    try {
      const { data } = await whatsappAPI.messages(jid, last);
      if (selectedRef.current?.jid === jid) mergeMessages(data || []);
    } catch (_) { /* noop */ }
  }, [mergeMessages]);

  /* ---------- tempo real ---------- */
  const reloadConvsSoon = useRef(null);
  const wsConnected = useWaRealtime(useCallback((ev) => {
    if (ev.type === 'wa_message' || ev.type === 'wa_conversation') setQuadroTick(t => t + 1);
    if (ev.type === 'wa_message') {
      if (selectedRef.current?.jid === ev.jid) {
        mergeMessages([ev.message]);
        if (ev.message?.media_file || ev.message?.message_type === 'nota') setPainelTick(t => t + 1);
      }
      clearTimeout(reloadConvsSoon.current);
      reloadConvsSoon.current = setTimeout(loadConversations, 300);   // agrupa rajadas
    } else if (ev.type === 'wa_status') {
      setMessages(ms => ms.map(m => (m.id === ev.message_id ? { ...m, status_code: Math.max(m.status_code || 0, ev.status_code) } : m)));
    } else if (ev.type === 'wa_media') {
      const { type: _t, message_id: mid, ...fields } = ev;
      setMessages(ms => ms.map(m => (m.id === mid ? { ...m, ...fields } : m)));
    } else if (ev.type === 'wa_connection') {
      setWaStatus(ev.status);
    } else if (ev.type === 'wa_conversation' || ev.type === 'wa_cadastro') {
      if (ev.conversation && selectedRef.current?.jid === ev.jid) { setSelected(sel => ({ ...sel, ...ev.conversation })); setPainelTick(t => t + 1); }
      clearTimeout(reloadConvsSoon.current);
      reloadConvsSoon.current = setTimeout(loadConversations, 300);
    } else if (ev.type === 'wa_mencao') {
      carregarMencoes();                // som e aviso: sino do topo
    } else if (ev.type === 'wa_transferencia') {
      loadConversations();
    }
  }, [mergeMessages, loadConversations, carregarMencoes]), true, useCallback((reconexao) => {
    // voltou a conexão: busca o que chegou enquanto estava fora (sem recarregar a conversa inteira)
    if (reconexao) { loadConversations(); pollNewMessages(); }
  }, [loadConversations, pollNewMessages]));

  // Poll de reserva — intervalo FIXO, pausa com a aba escondida
  useEffect(() => {
    loadConversations(); loadStatus();
    const tick = () => { if (!document.hidden) loadConversations(); };
    const t1 = setInterval(tick, wsConnected ? POLL_CONVS_MS_WS : POLL_CONVS_MS);
    const t2 = setInterval(() => { if (!document.hidden) loadStatus(); }, 20000);
    const onVis = () => { if (!document.hidden) { loadConversations(); pollNewMessages(); } };
    document.addEventListener('visibilitychange', onVis);
    return () => { clearInterval(t1); clearInterval(t2); document.removeEventListener('visibilitychange', onVis); };
  }, [wsConnected, loadConversations, loadStatus, pollNewMessages]);

  useEffect(() => { convsRef.current = []; setConversations([]); loadConversations(); }, [aba, filtros, loadConversations]);

  useEffect(() => { setConversaAberta(selected?.jid); }, [selected?.jid]);
  useEffect(() => () => setConversaAberta(null), []);

  useEffect(() => {
    if (!selected) return undefined;
    setMessages([]); setNovasAbaixo(0); setModoNota(false); setShowMenu(false); setResp({ aberto: false, filtro: '', idx: 0 }); setPainelSobre(false); setArroba(null); setSug(null);
    if (mencoes.itens.some(m => m.jid === selected.jid && !m.lida)) whatsappAPI.mencoesLidas({ jid: selected.jid }).then(carregarMencoes).catch(() => {});
    whatsappAPI.alertasLidas({ jid: selected.jid })
      .then(() => window.dispatchEvent(new Event('wa-alertas-atualizar'))).catch(() => {});
    loadMessages(selected.jid);
    inputRef.current?.focus();
    return undefined;
  }, [selected?.jid]); // eslint-disable-line react-hooks/exhaustive-deps

  // mensagens novas da conversa aberta: com WebSocket chegam na hora; o intervalo é a rede de segurança
  useEffect(() => {
    if (!selected?.jid) return undefined;
    const t = setInterval(() => { if (!document.hidden) pollNewMessages(); }, wsConnected ? POLL_MSGS_MS_WS : POLL_MSGS_MS);
    return () => clearInterval(t);
  }, [selected?.jid, wsConnected, pollNewMessages]);

  /* ---------- envio otimista ---------- */
  const doSend = async (jid, body, tempId) => {
    try {
      const { data } = await whatsappAPI.send(jid, body);
      if (data.jid && data.jid !== jid) {
        // backend unificou a conversa no JID oficial (ex.: número sem o nono dígito)
        setSelected(sel => (sel?.jid === jid ? { ...sel, jid: data.jid, numero: data.jid.split('@')[0] } : sel));
        loadConversations();
      }
      setMessages(ms => {
        const semTemp = ms.filter(m => m.id !== tempId);
        if (semTemp.some(m => m.id === data.id)) return semTemp;       // já chegou pelo WebSocket
        return [...semTemp, { ...(data.message || {}), id: data.id, status_code: data.message?.status_code || 2 }];
      });
    } catch (e) {
      const err = e?.response?.data?.detail;
      setMessages(ms => ms.map(m => (m.id === tempId ? { ...m, _failed: err || 'falha' } : m)));
      if (e?.response?.status === 503) { toast.error('WhatsApp reconectando — toque em "tentar de novo" em instantes'); loadStatus(); }
      else if (e?.response?.status === 409) toast.error(err);
      else if (err === 'numero_nao_tem_whatsapp') toast.error('Este número não tem WhatsApp');
      else toast.error('Falha ao enviar');
    }
  };

  const send = async () => {
    const body = text.trim();
    if (!body || !selected || sendingRef.current) return;
    if (modoNota) {                                    // nota interna: não vai para o cliente
      setText('');
      const ids = equipeLista.filter(u => mencoesSel.current.includes(u.id) && body.includes(`@${u.nome}`)).map(u => u.id);
      mencoesSel.current = []; setArroba(null);
      try {
        const { data } = await whatsappAPI.nota(selected.jid, body, ids); mergeMessages([data]);
        if (data.mencoes?.length) toast.success(`Nota enviada; ${data.mencoes.map(m => m.nome.split(' ')[0]).join(', ')} ${data.mencoes.length > 1 ? 'foram avisados' : 'foi avisado(a)'}`);
      } catch (e) { setText(body); toast.error(errMsg(e, 'Falha ao salvar nota')); }
      return;
    }
    sendingRef.current = true;
    const tempId = `tmp-${Date.now()}`;
    setMessages(ms => [...ms, {
      id: tempId, _temp: true, jid: selected.jid, text: body, from_me: true, message_type: 'text',
      timestamp: new Date().toISOString(), status_code: 1, sent_by_name: user?.nome || 'Você',
    }]);
    setText('');
    setAtBottom(true); scrollToBottom(true);
    try { await doSend(selected.jid, body, tempId); } finally { sendingRef.current = false; }
    inputRef.current?.focus();
  };

  /* ---------- arquivos e voz ---------- */
  const enviarArquivo = async (jid, arquivo, { legenda = '', voz = false, duracao } = {}, tempIdExistente) => {
    const tempId = tempIdExistente || `tmp-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    const tipo = voz ? 'audio' : tipoDe(arquivo);
    envios.current[tempId] = { arquivo, legenda, voz, duracao };
    if (!tempIdExistente) {
      setMessages(ms => [...ms, {
        id: tempId, _temp: true, _progresso: 0, _localUrl: URL.createObjectURL(arquivo), jid, from_me: true,
        message_type: tipo, voz, _duracao: duracao, media_filename: arquivo.name, media_size: arquivo.size, media_mime: arquivo.type,
        text: legenda || (tipo === 'document' ? `[documento] ${arquivo.name}` : ''),
        timestamp: new Date().toISOString(), status_code: 1, sent_by_name: user?.nome || 'Você',
      }]);
      setAtBottom(true); scrollToBottom(true);
    } else {
      setMessages(ms => ms.map(m => (m.id === tempId ? { ...m, _failed: null, _progresso: 0 } : m)));
    }
    try {
      const { data } = await whatsappAPI.sendMedia(jid, arquivo, { legenda, voz },
        (p) => setMessages(ms => ms.map(m => (m.id === tempId ? { ...m, _progresso: p } : m))));
      delete envios.current[tempId];
      setMessages(ms => {
        const velho = ms.find(m => m.id === tempId);
        if (velho?._localUrl) setTimeout(() => URL.revokeObjectURL(velho._localUrl), 60000);
        const semTemp = ms.filter(m => m.id !== tempId);
        if (semTemp.some(m => m.id === data.id)) return semTemp;       // já chegou pelo tempo real
        return [...semTemp, { ...(data.message || {}), id: data.id, status_code: data.message?.status_code || 2 }];
      });
      if (data.jid && data.jid !== jid) loadConversations();
    } catch (e) {
      const st = e?.response?.status;
      const err = st === 413 ? 'Arquivo grande demais para o servidor' : (e?.response?.data?.detail || 'falha');
      setMessages(ms => ms.map(m => (m.id === tempId ? { ...m, _failed: err } : m)));
      if (st === 503) { toast.error('WhatsApp reconectando — toque em "tentar de novo" em instantes'); loadStatus(); }
      else if (st === 409) toast.error(e.response.data.detail);
      else if (st === 413) toast.error('Arquivo grande demais para o servidor');
      else toast.error(`Não foi possível enviar ${voz ? 'o áudio' : arquivo.name}`);
    }
  };
  const gravador = useGravador(useCallback((arquivo, duracao) => {
    const jid = selectedRef.current?.jid;
    if (!jid) return;
    if (duracao < 0.8) { toast.info('Áudio curto demais'); return; }
    enviarArquivo(jid, arquivo, { voz: true, duracao });
  }, [])); // eslint-disable-line react-hooks/exhaustive-deps
  const escolherArquivos = (lista) => {
    const ok = aceitarArquivos(lista);
    if (ok.length) setAnexos(a => [...a, ...ok].slice(0, 30));
  };
  const enviarAnexos = (itens) => {
    const jid = selected?.jid;
    setAnexos([]);
    if (!jid) return;
    (async () => { for (const it of itens) await enviarArquivo(jid, it.arquivo, { legenda: it.legenda }); })();
  };
  const podeAnexar = () => !!selectedRef.current && !modoNota && !bloqueadoRef.current;
  const onPaste = (e) => {
    if (!podeAnexar()) return;
    const fs = Array.from(e.clipboardData?.files || []);
    if (fs.length) { e.preventDefault(); escolherArquivos(fs); }
  };

  const retry = (m) => {
    if (envios.current[m.id]) {
      const { arquivo, legenda, voz, duracao } = envios.current[m.id];
      enviarArquivo(m.jid, arquivo, { legenda, voz, duracao }, m.id);
      return;
    }
    setMessages(ms => ms.map(x => (x.id === m.id ? { ...x, _failed: null } : x)));
    doSend(m.jid, m.text, m.id);
  };

  const primeiroNome = (n = '') => { const p = n.trim().split(/\s+/)[0] || ''; return p ? p[0].toUpperCase() + p.slice(1).toLowerCase() : ''; };
  const escolherResposta = async (t) => {
    let txt = t.conteudo || '';
    txt = txt.replaceAll('{{nome_cliente}}', primeiroNome(selected?.cliente_nome || selected?.push_name || ''));
    if (/{{(numero_projeto|etapa_atual)}}/.test(txt)) {
      try {
        const { data } = await whatsappAPI.painel(selected.jid);
        const p = (data.projetos || []).find(x => ['em_andamento', 'suspenso'].includes(x.status)) || {};
        txt = txt.replaceAll('{{numero_projeto}}', p.numero_interno || '').replaceAll('{{etapa_atual}}', p.etapa_atual_nome || '');
      } catch (_) { /* deixa as variáveis para o atendente completar */ }
    }
    setText(txt); setResp({ aberto: false, filtro: '', idx: 0 });
    requestAnimationFrame(() => inputRef.current?.focus());
  };
  const candidatosArroba = useMemo(() => {
    if (!arroba) return [];
    const t = arroba.termo.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    return equipeLista.filter(u => u.id !== user?.id && u.nome.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().includes(t)).slice(0, 6);
  }, [arroba, equipeLista, user]);
  const escolherArroba = (u) => {
    const el = inputRef.current; const pos = el?.selectionStart ?? text.length;
    const antes = text.slice(0, pos).replace(/@([^\s@]*)$/, `@${u.nome} `);
    setText(antes + text.slice(pos));
    if (!mencoesSel.current.includes(u.id)) mencoesSel.current.push(u.id);
    setArroba(null);
    requestAnimationFrame(() => { el?.focus(); el?.setSelectionRange(antes.length, antes.length); });
  };
  const pedirSugestoes = async () => {
    if (sug === 'carregando') return;
    if (Array.isArray(sug)) { setSug(null); return; }
    setSug('carregando');
    try { const { data } = await whatsappAPI.iaSugerir(selected.jid); setSug(data.sugestoes?.length ? data.sugestoes : []); }
    catch (e) { setSug(null); toast.error(errMsg(e, 'Não foi possível gerar sugestões')); }
  };
  const onChangeTexto = (e) => {
    const v = e.target.value; setText(v);
    if (modoNota) {
      const m = v.slice(0, e.target.selectionStart ?? v.length).match(/(?:^|\s)@([^\s@]*)$/);
      if (m) {
        if (!equipeLista.length) whatsappAPI.atendentes().then(r => setEquipeLista(r.data || [])).catch(() => {});
        setArroba({ termo: m[1], idx: 0 });
      } else if (arroba) setArroba(null);
      return;
    }
    if (!modoNota && v.startsWith('/') && !v.includes('\n')) setResp({ aberto: true, filtro: v.slice(1), idx: 0 });
    else if (resp.aberto && !resp.viaBotao) setResp({ aberto: false, filtro: '', idx: 0 });
  };
  const onKeyDown = (e) => {
    if (arroba && candidatosArroba.length) {
      if (e.key === 'ArrowDown') { e.preventDefault(); setArroba(a => ({ ...a, idx: Math.min(a.idx + 1, candidatosArroba.length - 1) })); return; }
      if (e.key === 'ArrowUp') { e.preventDefault(); setArroba(a => ({ ...a, idx: Math.max(a.idx - 1, 0) })); return; }
      if (e.key === 'Escape') { e.preventDefault(); setArroba(null); return; }
      if ((e.key === 'Enter' || e.key === 'Tab') && !e.shiftKey) { e.preventDefault(); escolherArroba(candidatosArroba[arroba.idx] || candidatosArroba[0]); return; }
    }
    if (resp.aberto) {
      if (e.key === 'ArrowDown') { e.preventDefault(); setResp(r => ({ ...r, idx: Math.min(r.idx + 1, Math.max(0, respItens.length - 1)) })); return; }
      if (e.key === 'ArrowUp') { e.preventDefault(); setResp(r => ({ ...r, idx: Math.max(r.idx - 1, 0) })); return; }
      if (e.key === 'Escape') { e.preventDefault(); setResp({ aberto: false, filtro: '', idx: 0 }); return; }
      if (e.key === 'Enter' && !e.shiftKey && respItens[resp.idx]) { e.preventDefault(); escolherResposta(respItens[resp.idx]); return; }
    }
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) { e.preventDefault(); send(); }
  };
  useEffect(() => {   // textarea que cresce até 6 linhas
    const el = inputRef.current; if (!el) return;
    el.style.height = 'auto'; el.style.height = `${Math.min(el.scrollHeight, 140)}px`;
  }, [text]);

  const executar = async (nome, fn, ok) => {
    setAcao(nome); setShowMenu(false);
    try { await fn(); if (ok) toast.success(ok); await loadConversations(); }
    catch (e) { toast.error(errMsg(e, 'Não foi possível concluir')); }
    finally { setAcao(null); }
  };
  const sincronizarGrupos = () => executar('grupos', async () => {
    const { data } = await whatsappAPI.sincronizarGrupos();
    toast.success(data.total ? `${data.total} grupo(s) do WhatsApp atualizados` : 'Este número não participa de nenhum grupo');
  });
  const assumir = () => executar('assumir', () => whatsappAPI.assumir(selected.jid), 'Atendimento assumido');
  const finalizar = () => { setShowMenu(false); setShowFinalizar(true); };
  const confirmarFinalizar = async (avaliacao) => {
    const jid = selected.jid;
    setAcao('finalizar');
    try {
      await whatsappAPI.finalizar(jid, { avaliacao });
      toast.success('Atendimento finalizado');
      setShowFinalizar(false);
      // finalizada sai da lista: fecha a conversa (o histórico fica na ficha e no cadastro do cliente)
      setConversations(cs => cs.filter(c => c.jid !== jid));
      if (selectedRef.current?.jid === jid) setSelected(null);
      loadConversations();
    } catch (e) { toast.error(errMsg(e, 'Não foi possível finalizar')); }
    finally { setAcao(null); }
  };
  const arquivar = () => executar('arquivar', () => whatsappAPI.arquivar(selected.jid), 'Conversa arquivada');
  const reabrir = () => executar('reabrir', () => whatsappAPI.reabrir(selected.jid), 'Conversa reaberta com você');
  const pedirCadastro = () => {
    if (!window.confirm('Enviar o formulário de cadastro para este contato?\nO assistente faz as perguntas e, no fim, a conversa volta para você.')) return;
    executar('cadastro', () => whatsappAPI.pedirCadastro(selected.jid), 'Formulário enviado ao cliente');
  };
  const transferir = async (paraId, nota, setorId) => {
    await executar('transferir', () => whatsappAPI.transferir(selected.jid, paraId, nota, setorId),
      paraId ? 'Atendimento transferido' : 'Encaminhado para a fila do setor');
    setShowTransfer(false);
  };
  const mudarSituacao = (sit) => executar('situacao', async () => {
    const { data } = await whatsappAPI.situacao(selected.jid, sit);
    setSelected(sel => ({ ...sel, ...data }));
  });
  const salvarEtiquetas = async (ids) => {
    const antes = selected.etiquetas;
    setSelected(sel => ({ ...sel, etiquetas: ids }));          // otimista
    try { await whatsappAPI.etiquetasConversa(selected.jid, ids); loadConversations(); }
    catch (e) { setSelected(sel => ({ ...sel, etiquetas: antes })); toast.error(errMsg(e, 'Não foi possível salvar as etiquetas')); }
  };

  const excluirConversa = async (jid, nome) => {
    if (!window.confirm(`Excluir conversa com ${nome}?\n(A conversa some daqui, mas continua no WhatsApp)`)) return;
    try {
      await whatsappAPI.deleteConversation(jid);
      if (selected?.jid === jid) setSelected(null);
      toast.success('Conversa excluída');
      loadConversations();
    } catch (_) { toast.error('Falha ao excluir'); }
  };

  const abrirDaBusca = async (jid, msgId) => {
    pularParaRef.current = msgId || null;
    const daLista = convsRef.current.find(c => c.jid === jid);
    if (daLista && selected?.jid !== jid) { setSelected(daLista); return; }
    try {
      const { data } = await whatsappAPI.painel(jid);
      if (selected?.jid === jid) { pularParaRef.current = msgId || null; loadMessages(jid); }
      setSelected(data.conversa);
    } catch (_) { toast.error('Não foi possível abrir a conversa'); }
  };

  useEffect(() => {
    const jid = params.get('jid');
    if (!jid) return;
    setModo('lista');
    abrirDaBusca(jid);
    params.delete('jid'); setParams(params, { replace: true });
  }, [params]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ---------- derivados ---------- */
  const buscando = search.trim().length >= 2;
  const filtered = conversations;

  const conectado = waStatus === 'connected';
  const stSel = selected?.atendimento_status || 'bot';
  const grupo = !!selected?.is_grupo;   // grupo: aberto para toda a equipe, sem assumir/transferir/finalizar
  const meu = stSel === 'em_atendimento' && selected?.atendente_id === user?.id;
  const comOutro = stSel === 'em_atendimento' && selected?.atendente_id && !meu;
  const bloqueado = comOutro && !respondeTodas && !modoNota;
  bloqueadoRef.current = bloqueado;
  // finalizar não exige assumir: fila, triagem ou em atendimento (a de outra pessoa só para quem supervisiona
  // ou quando atendentes respondem todas); operador só as dele
  const podeFinalizar = !grupo && ['em_atendimento', 'fila', 'bot'].includes(stSel)
    && (operador ? meu : (meu || admin || !comOutro || respondeTodas));

  /* ---------- render ---------- */
  if (modo === 'quadro') {
    return (
      <>
        <KanbanAtendimento tick={quadroTick} userId={user?.id} setores={setores} etiquetas={etiquetas}
          setoresMap={setoresMap} etiquetasMap={etiquetasMap} Avatar={Avatar}
          onVoltar={() => setModo('lista')} onAbrir={(jid) => { setModo('lista'); abrirDaBusca(jid); }} />
      </>
    );
  }
  return (
    <div className={`grid grid-cols-1 md:grid-cols-[340px_1fr] ${selected && !selected.is_grupo && painelAberto && largo ? '2xl:grid-cols-[340px_1fr_340px]' : ''} h-[calc(100dvh-10rem)] min-h-[520px] rounded-2xl overflow-hidden [--wa-anel-fundo:#fff] dark:[--wa-anel-fundo:#0f172a] border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm`} data-testid="atendimento-page">
      {/* ===== Lista ===== */}
      <aside className={`flex flex-col min-h-0 border-r border-slate-200 dark:border-slate-800 ${selected ? 'hidden md:flex' : 'flex'}`}>
        <div className="px-4 pt-4 pb-3 space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-lg font-bold leading-tight">Atendimento</h1>
              <ConnectionPill status={waStatus} />
            </div>
            <div className="flex items-center gap-1">
              <div className="relative">
                <button onClick={() => setShowMencoes(v => !v)} title="Menções a você" data-testid="wa-mencoes-btn"
                  className="relative h-9 w-9 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-300 flex items-center justify-center">
                  <AtSign className="w-5 h-5" />
                  {mencoes.nao_lidas > 0 && <span className="absolute top-0.5 right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[10px] font-semibold flex items-center justify-center" data-testid="wa-mencoes-badge">{mencoes.nao_lidas}</span>}
                </button>
                {showMencoes && (
                  <div className="absolute left-0 top-11 z-40 w-80 max-h-96 overflow-y-auto rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-xl" data-testid="wa-mencoes-lista">
                    <div className="px-3 py-2 border-b border-slate-100 dark:border-slate-800 flex items-center gap-2 text-sm font-semibold">
                      <Bell className="w-4 h-4" /> Menções a você
                      {mencoes.nao_lidas > 0 && (
                        <button className="ml-auto text-xs font-normal text-emerald-700 dark:text-emerald-400 hover:underline"
                          onClick={() => whatsappAPI.mencoesLidas({}).then(carregarMencoes)}>marcar todas como lidas</button>
                      )}
                    </div>
                    {!mencoes.itens.length && <p className="px-3 py-6 text-sm text-center text-slate-500">Quando um colega escrever @{(user?.nome || 'você').split(' ')[0]} numa nota interna, aparece aqui.</p>}
                    {mencoes.itens.map(m => (
                      <button key={m.id} onClick={() => { setShowMencoes(false); abrirDaBusca(m.jid, m.msg_id); whatsappAPI.mencoesLidas({ ids: [m.id] }).then(carregarMencoes); }}
                        className={`w-full text-left px-3 py-2.5 border-b border-slate-100 dark:border-slate-800 last:border-0 hover:bg-slate-50 dark:hover:bg-slate-800 ${m.lida ? '' : 'bg-amber-50/60 dark:bg-amber-900/20'}`}>
                        <p className="text-[13px]"><b>{m.de}</b> em <b>{m.conversa}</b></p>
                        <p className="text-xs text-slate-600 dark:text-slate-300 line-clamp-2">{m.texto}</p>
                        <p className="text-[11px] text-slate-400 mt-0.5">{fmtListTime(m.at)}</p>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <button onClick={() => setModo('quadro')} title="Ver em quadro (Kanban)" data-testid="wa-modo-quadro"
                className="h-9 w-9 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-300 flex items-center justify-center">
                <SquareKanban className="w-5 h-5" />
              </button>
              <button onClick={() => setShowNova(true)} title="Nova mensagem" data-testid="btn-nova-mensagem-atendimento"
                className="h-9 w-9 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-300 flex items-center justify-center">
                <MessageSquarePlus className="w-5 h-5" />
              </button>
            </div>
          </div>
          <div className="relative">
            <Search className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Nome, telefone, CPF, protocolo ou palavra" data-testid="wa-search"
              onKeyDown={e => e.key === 'Escape' && setSearch('')}
              className="w-full h-9 pl-9 pr-8 rounded-lg bg-slate-100 dark:bg-slate-800 text-sm outline-none focus:ring-2 focus:ring-emerald-500/30" />
            {search && (
              <button onClick={() => setSearch('')} className="absolute right-2 top-2 h-5 w-5 rounded-full text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 flex items-center justify-center" aria-label="Limpar busca" data-testid="wa-search-limpar">
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
          {!buscando && <>
          <div className="flex gap-1.5 text-xs font-medium overflow-x-auto -mx-1 px-1 pb-0.5">
            {ABAS.filter(([k]) => !operador || ['meus', 'grupos', 'arquivadas'].includes(k)).map(([k, l]) => {
              const n = contadores[k];
              const alerta = (k === 'fila' || k === 'cadastros') && n > 0;
              return (
                <button key={k} onClick={() => setAba(k)} data-testid={`wa-aba-${k}`}
                  className={`px-3 py-1 rounded-full whitespace-nowrap transition-colors flex items-center gap-1 ${aba === k ? 'bg-emerald-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300'}`}>
                  {l}
                  {n > 0 && <span className={`rounded-full px-1.5 text-[10px] ${aba === k ? 'bg-white/25' : alerta ? 'bg-amber-500 text-white' : 'bg-slate-300/70'}`}>{n}</span>}
                </button>
              );
            })}
          </div>
          <FiltrosBar filtros={filtros} setFiltros={setFiltros} aberto={showFiltros} setAberto={setShowFiltros} comEtapa={aba === 'ativas'}
            setores={setores} etiquetas={etiquetas} setoresMap={setoresMap} etiquetasMap={etiquetasMap}
            filaSetores={aba === 'fila' ? contadores.fila_setores : null} />
          </>}
        </div>

        <div className="flex-1 overflow-y-auto">
          {buscando && (
            <BuscaGlobal termo={search} avatar={Avatar}
              onAbrirConversa={(c) => abrirDaBusca(c.jid)} onAbrirMensagem={(m) => abrirDaBusca(m.jid, m.id)} />
          )}
          {!buscando && <>
          {filtered.length === 0 && (filtros.setor || filtros.etiqueta || filtros.situacao) && (
            <p className="p-6 text-sm text-center text-slate-500">Nenhuma conversa com esses filtros</p>
          )}
          {filtered.length === 0 && !(filtros.setor || filtros.etiqueta || filtros.situacao) && (
            <p className="p-6 text-sm text-center text-slate-500">
              {{ meus: 'Nenhum atendimento com você agora', fila: 'Ninguém aguardando na fila 🎉', cadastros: 'Nenhum cadastro para validar', arquivadas: 'Nenhuma conversa arquivada', grupos: 'Nenhum grupo ainda' }[aba] || 'Nenhuma conversa'}
            </p>
          )}
          {aba === 'grupos' && (
            <div className="px-4 pb-2 flex justify-center">
              <button onClick={sincronizarGrupos} disabled={acao === 'grupos'} data-testid="wa-grupos-sincronizar"
                className="h-8 px-3 rounded-lg border text-[12.5px] font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 flex items-center gap-1.5 disabled:opacity-60">
                {acao === 'grupos' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCw className="w-3.5 h-3.5" />} Buscar grupos do WhatsApp
              </button>
            </div>
          )}
          {filtered.map(c => (
            <ConversationItem key={c.jid} c={c} active={selected?.jid === c.jid} userId={user?.id}
              setor={setoresMap[c.setor_id]} etiquetasMap={etiquetasMap}
              onSelect={() => setSelected(c)} onDelete={() => excluirConversa(c.jid, displayName(c))} />
          ))}
          </>}
        </div>
      </aside>

      {/* ===== Chat ===== */}
      <section className={`relative flex flex-col min-h-0 min-w-0 ${selected ? 'flex' : 'hidden md:flex'}`}>
        {waStatus && !conectado && <ConnectionBanner status={waStatus} />}

        {!selected ? (
          <div className="flex-1 flex flex-col items-center justify-center gap-2 text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-950 px-6 text-center">
            <MessageCircle className="w-12 h-12 text-slate-300 dark:text-slate-700" />
            <p className="text-sm font-medium text-slate-600 dark:text-slate-300">Escolha uma conversa</p>
            <p className="text-xs max-w-xs">Ou use a busca para encontrar qualquer contato pelo nome, CPF, telefone, protocolo ou por algo que foi dito.</p>
            <button onClick={() => setShowNova(true)} className="mt-2 h-8 px-3 rounded-lg border text-[13px] font-medium hover:bg-white dark:hover:bg-slate-900 flex items-center gap-1.5">
              <MessageSquarePlus className="w-4 h-4" /> Nova conversa
            </button>
          </div>
        ) : (
          <>
            <header className="h-16 px-3 sm:px-4 flex items-center gap-2 sm:gap-3 border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
              <button className="md:hidden -ml-1 h-9 w-9 rounded-lg text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800" onClick={() => setSelected(null)} aria-label="Voltar">←</button>
              <button onClick={grupo ? undefined : alternarPainel} className="rounded-full shrink-0" title={grupo ? undefined : 'Ficha do contato'} aria-label="Ficha do contato">
                <Avatar name={displayName(selected)} size={40} anel={anelEstado(selected, user?.id)} />
              </button>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 min-w-0">
                  <p className="font-semibold truncate">{displayName(selected)}</p>
                  {selected.cliente_id && <span className="text-[11px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-200 font-medium shrink-0">Cliente</span>}
                </div>
                <div className="text-xs text-slate-500 dark:text-slate-400 flex items-center gap-2 min-w-0">
                  <StatusChip conv={selected} userId={user?.id} />
                  {selected.protocolo && <span className="shrink-0 font-medium text-slate-600 dark:text-slate-300" data-testid="wa-protocolo">{fmtProtocolo(selected.protocolo)}</span>}
                  <span className="truncate hidden sm:inline" title={ehLid(selected) ? 'O WhatsApp não mostrou o número deste contato. Ele aparece quando o contato escrever de novo ou se salvar o número.' : undefined}>
                    {grupo ? (selected.grupo_participantes ? `${selected.grupo_participantes} participantes` : '') : telefoneConversa(selected) || (ehLid(selected) ? 'número oculto pelo WhatsApp' : '')}
                  </span>
                </div>
              </div>
              {grupo ? null : stSel === 'encerrada' ? (
                <HeaderBtn compacto={painelAberto && largo} onClick={reabrir} loading={acao === 'reabrir'} icon={ArchiveRestore} primary>Reabrir</HeaderBtn>
              ) : !meu && !operador && (!comOutro || admin) ? (
                <HeaderBtn compacto={painelAberto && largo} onClick={assumir} loading={acao === 'assumir'} icon={UserCheck} primary testid="wa-assumir">Assumir</HeaderBtn>
              ) : null}
              {!grupo && (meu || admin || stSel === 'fila' || stSel === 'bot') && stSel !== 'encerrada' && (
                <HeaderBtn compacto={painelAberto && largo} onClick={() => setShowTransfer('atendente')} loading={acao === 'transferir'} icon={ArrowRightLeft} testid="wa-transferir">Transferir</HeaderBtn>
              )}
              {podeFinalizar && (
                <HeaderBtn compacto={painelAberto && largo} onClick={finalizar} loading={acao === 'finalizar'} icon={CheckCircle2} testid="wa-finalizar">Finalizar</HeaderBtn>
              )}
              {!grupo && <button onClick={alternarPainel} title={painelAberto ? 'Fechar ficha do contato' : 'Abrir ficha do contato'} data-testid="wa-painel-toggle"
                aria-pressed={painelAberto}
                className={`h-9 w-9 rounded-lg flex items-center justify-center shrink-0 ${painelAberto ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300' : 'text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800'}`}>
                <PanelRight className="w-[18px] h-[18px]" />
              </button>}
              <div className="relative">
                <button onClick={() => setShowMenu(v => !v)} title="Mais ações" data-testid="wa-mais"
                  className="h-9 w-9 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500 flex items-center justify-center">
                  <MoreVertical className="w-4 h-4" />
                </button>
                {showMenu && (
                  <div className="absolute right-0 top-10 z-40 w-60 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-lg py-1 text-sm">
                    {!grupo && <div className="sm:hidden border-b border-slate-100 dark:border-slate-800 mb-1 pb-1">
                      {stSel === 'encerrada' && <MenuItem icon={ArchiveRestore} onClick={reabrir}>Reabrir</MenuItem>}
                      {stSel !== 'encerrada' && !meu && !operador && (!comOutro || admin) && <MenuItem icon={UserCheck} onClick={assumir}>Assumir</MenuItem>}
                      {(meu || admin || stSel === 'fila' || stSel === 'bot') && stSel !== 'encerrada' && <MenuItem icon={ArrowRightLeft} onClick={() => { setShowMenu(false); setShowTransfer('atendente'); }}>Transferir</MenuItem>}
                      {podeFinalizar && <MenuItem icon={CheckCircle2} onClick={finalizar}>Finalizar</MenuItem>}
                    </div>}
                    {selected.cliente_id && (
                      <Link to={`/clientes/${selected.cliente_id}`} className="w-full text-left px-3 py-2 flex items-center gap-2 hover:bg-slate-50 dark:hover:bg-slate-800">
                        <User className="w-4 h-4" />Abrir cadastro do cliente
                      </Link>
                    )}
                    {!grupo && !selected.cliente_id && stSel !== 'encerrada' && (
                      <MenuItem icon={ClipboardList} onClick={pedirCadastro}>Pedir cadastro ao cliente</MenuItem>
                    )}
                    {!grupo && stSel !== 'encerrada' && <MenuItem icon={Archive} onClick={arquivar}>Arquivar (sem avisar o cliente)</MenuItem>}
                    <MenuItem icon={Trash2} danger onClick={() => { setShowMenu(false); excluirConversa(selected.jid, displayName(selected)); }}>Excluir conversa</MenuItem>
                  </div>
                )}
              </div>
            </header>
            <OrganizacaoBar conv={selected} setor={setoresMap[selected.setor_id]} etiquetas={etiquetas} etiquetasMap={etiquetasMap}
              podeSituacao={stSel === 'em_atendimento' && (meu || admin)} onSituacao={mudarSituacao} carregando={acao === 'situacao'}
              onEtiquetas={salvarEtiquetas}
              onSetor={!grupo && (meu || admin || stSel === 'fila' || stSel === 'bot') && stSel !== 'encerrada' && setores.length ? () => setShowTransfer('setor') : null} />
            {selected.cadastro_pendente_id && (
              <CadastroCard cadastroId={selected.cadastro_pendente_id} onDone={loadConversations} />
            )}

            <div className="relative flex-1 min-h-0"
              onDragEnter={e => { if (podeAnexar() && e.dataTransfer?.types?.includes('Files')) { e.preventDefault(); setArrastando(true); } }}
              onDragOver={e => { if (arrastando) e.preventDefault(); }}
              onDragLeave={e => { if (!e.currentTarget.contains(e.relatedTarget)) setArrastando(false); }}
              onDrop={e => { if (!arrastando) return; e.preventDefault(); setArrastando(false); escolherArquivos(e.dataTransfer.files); }}>
              {arrastando && (
                <div className="absolute inset-2 z-30 rounded-2xl border-2 border-dashed border-emerald-500 bg-emerald-50/90 dark:bg-emerald-950/80 flex flex-col items-center justify-center gap-2 text-emerald-800 dark:text-emerald-200 pointer-events-none"
                  data-testid="wa-soltar-arquivos">
                  <UploadCloud className="w-10 h-10" />
                  <p className="font-medium">Solte para enviar a {displayName(selected)}</p>
                </div>
              )}
              <div ref={scrollRef} onScroll={onScroll}
                className="absolute inset-0 overflow-y-auto px-[5%] py-4 bg-[#f1f3ee] dark:bg-slate-950 [background-image:radial-gradient(rgba(15,23,42,.045)_1px,transparent_1px)] dark:[background-image:radial-gradient(rgba(255,255,255,.035)_1px,transparent_1px)] [background-size:18px_18px]">
                {loadingMsgs && messages.length === 0 && <MessagesSkeleton />}
                {messages.map((m, i) => {
                  const prev = messages[i - 1];
                  const newDay = !prev || new Date(prev.timestamp).toDateString() !== new Date(m.timestamp).toDateString();
                  const first = newDay || !prev || prev.from_me !== m.from_me || (m.from_me && prev.sent_by_name !== m.sent_by_name)
                    || (!m.from_me && (prev.participante_numero || prev.participante_nome) !== (m.participante_numero || m.participante_nome));
                  return (
                    <React.Fragment key={m.id}>
                      {newDay && (
                        <div className="flex justify-center my-3">
                          <span className="text-[11.5px] font-medium bg-white/95 dark:bg-slate-800 text-slate-600 dark:text-slate-300 px-3 py-1 rounded-full shadow-sm">{fmtDay(m.timestamp)}</span>
                        </div>
                      )}
                      <MessageBubble m={m} first={first} onRetry={retry} destaque={destaqueMsg === m.id} />
                    </React.Fragment>
                  );
                })}
              </div>
              {!atBottom && (
                <button onClick={() => scrollToBottom(true)}
                  className="absolute right-5 bottom-4 h-10 min-w-10 px-3 rounded-full bg-white dark:bg-slate-800 shadow-md text-slate-600 dark:text-slate-200 flex items-center gap-1 text-sm font-medium">
                  {novasAbaixo > 0 && <span className="bg-emerald-500 text-white text-[11px] rounded-full px-1.5">{novasAbaixo}</span>}
                  <ChevronDown className="w-5 h-5" />
                </button>
              )}
            </div>

            {bloqueado && (
              <div className="px-4 py-2 text-xs bg-slate-100 dark:bg-slate-800 text-slate-600 border-t flex items-center gap-2">
                <Lock className="w-3.5 h-3.5 shrink-0" />
                <span>Em atendimento com <b>{selected.atendente_nome}</b>. Você pode deixar uma nota interna (ícone ao lado) ou pedir que transfiram para você.</span>
              </div>
            )}
            <footer className={`px-3 sm:px-4 py-3 flex items-end gap-1.5 border-t border-slate-200 dark:border-slate-800 ${modoNota ? 'bg-amber-50 dark:bg-amber-950/40' : 'bg-white dark:bg-slate-900'}`}>
              <button onClick={() => { setModoNota(v => !v); inputRef.current?.focus(); }} data-testid="wa-modo-nota"
                title={modoNota ? 'Voltar para mensagem ao cliente' : 'Nota interna (só a equipe vê)'}
                className={`h-10 w-10 shrink-0 rounded-full flex items-center justify-center ${modoNota ? 'bg-amber-400 text-amber-950' : 'text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800'}`}>
                <StickyNote className="w-5 h-5" />
              </button>
              {!modoNota && (
                <button onClick={() => { setResp(r => (r.aberto ? { aberto: false, filtro: '', idx: 0 } : { aberto: true, filtro: '', idx: 0, viaBotao: true })); inputRef.current?.focus(); }}
                  disabled={bloqueado} title="Respostas rápidas (ou digite / no começo)" data-testid="wa-respostas-btn"
                  className={`h-10 w-10 shrink-0 rounded-full flex items-center justify-center disabled:opacity-40 ${resp.aberto ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300' : 'text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800'}`}>
                  <Zap className="w-5 h-5" />
                </button>
              )}
              {!modoNota && gravador.estado !== 'gravando' && (
                <>
                  <button onClick={() => arquivoInput.current?.click()} disabled={!podeAnexar()} title="Anexar arquivo (ou arraste / cole na conversa)"
                    aria-label="Anexar arquivo" data-testid="wa-anexar-btn"
                    className="h-10 w-10 shrink-0 rounded-full flex items-center justify-center disabled:opacity-40 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800">
                    <Paperclip className="w-5 h-5" />
                  </button>
                  <input ref={arquivoInput} type="file" multiple hidden data-testid="wa-anexar-input"
                    onChange={e => { escolherArquivos(e.target.files); e.target.value = ''; }} />
                </>
              )}
              {!modoNota && ia?.ativa && ia?.sugestoes && gravador.estado !== 'gravando' && (
                <button onClick={pedirSugestoes} disabled={bloqueado} title="Sugerir resposta com IA" data-testid="wa-ia-sugerir"
                  className={`h-10 w-10 shrink-0 rounded-full flex items-center justify-center disabled:opacity-40 ${sug ? 'bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300' : 'text-violet-600 hover:bg-violet-50 dark:hover:bg-violet-900/30'}`}>
                  {sug === 'carregando' ? <Loader2 className="w-5 h-5 animate-spin" /> : <Sparkles className="w-5 h-5" />}
                </button>
              )}
              {gravador.estado === 'gravando' ? <BarraGravacao gravador={gravador} /> : (
              <div className="relative flex-1 min-w-0 flex">
              {Array.isArray(sug) && !modoNota && (
                <div className="absolute bottom-full left-0 right-0 mb-2 rounded-xl border border-violet-200 dark:border-violet-900 bg-white dark:bg-slate-900 shadow-xl overflow-hidden z-20" data-testid="wa-ia-sugestoes">
                  <div className="px-3 py-2 border-b border-slate-100 dark:border-slate-800 text-xs text-slate-500 flex items-center gap-1.5">
                    <Sparkles className="w-3.5 h-3.5 text-violet-500" /> Sugestões da IA: clique para usar e revise antes de enviar
                    <button onClick={() => setSug(null)} className="ml-auto text-slate-400 hover:text-slate-700" aria-label="Fechar"><X className="w-3.5 h-3.5" /></button>
                  </div>
                  {!sug.length && <p className="px-3 py-3 text-sm text-slate-500">Sem sugestão para esta conversa.</p>}
                  {sug.map((t, i) => (
                    <button key={i} type="button" onMouseDown={ev => { ev.preventDefault(); setText(t); setSug(null); requestAnimationFrame(() => inputRef.current?.focus()); }}
                      className="w-full text-left px-3 py-2.5 text-sm border-b border-slate-100 dark:border-slate-800 last:border-0 hover:bg-violet-50 dark:hover:bg-violet-900/20 whitespace-pre-wrap" data-testid={`wa-ia-sug-${i}`}>
                      {t}
                    </button>
                  ))}
                </div>
              )}
              {resp.aberto && !modoNota && (
                <RespostasRapidas itens={respItens} ativo={resp.idx} filtro={resp.filtro} onEscolher={escolherResposta} />
              )}
              {modoNota && arroba && (
                <div className="absolute bottom-full left-0 mb-2 w-72 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-xl overflow-hidden z-20" data-testid="wa-arroba">
                  <p className="px-3 py-2 border-b border-slate-100 dark:border-slate-800 text-xs text-slate-500 flex items-center gap-1.5"><AtSign className="w-3.5 h-3.5" /> Mencionar colega (recebe aviso)</p>
                  {!candidatosArroba.length ? <p className="px-3 py-2 text-sm text-slate-500">Ninguém com esse nome</p> : candidatosArroba.map((u, i) => (
                    <button key={u.id} type="button" onMouseDown={ev => { ev.preventDefault(); escolherArroba(u); }} data-testid={`wa-arroba-${u.id}`}
                      className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 ${i === arroba.idx ? 'bg-amber-50 dark:bg-amber-900/30' : 'hover:bg-slate-50 dark:hover:bg-slate-800'}`}>
                      <Avatar name={u.nome} size={24} />{u.nome}
                      <span className="ml-auto text-[11px] text-slate-400">{u.presenca === 'online' ? 'online' : ''}</span>
                    </button>
                  ))}
                </div>
              )}
              <textarea ref={inputRef} rows={1} value={text} onChange={onChangeTexto} onKeyDown={onKeyDown} onPaste={onPaste}
                onBlur={() => setTimeout(() => setResp(r => (r.aberto ? { aberto: false, filtro: '', idx: 0 } : r)), 150)}
                disabled={bloqueado}
                placeholder={modoNota ? 'Nota interna — o cliente NÃO vê. Use @ para avisar um colega' : bloqueado ? `Conversa com ${selected.atendente_nome}` : !conectado ? (['connecting', 'reconnecting'].includes(waStatus) ? 'Reconectando… se falhar, você pode reenviar' : 'WhatsApp desconectado — a mensagem pode falhar')
                  : grupo ? `Mensagem para o grupo (vai assinada como ${user?.nome || 'você'})` : stSel !== 'em_atendimento' || !meu ? 'Ao responder, você assume este atendimento' : `Mensagem (vai assinada como ${user?.nome || 'você'})`}
                data-testid="wa-msg-input"
                className={`flex-1 resize-none rounded-xl px-4 py-2.5 text-[15px] leading-5 outline-none max-h-[140px] disabled:opacity-50 focus:ring-2 ${modoNota ? 'bg-amber-100/70 dark:bg-amber-900/30 placeholder:text-amber-700 focus:ring-amber-400/40' : 'bg-slate-100 dark:bg-slate-800 focus:ring-emerald-500/25'}`} />
              </div>
              )}
              {gravador.estado === 'gravando' ? null : !modoNota && !text.trim() ? (
                <BotaoMic gravador={gravador} disabled={bloqueado} />
              ) : (
              <button onClick={send} disabled={!text.trim() || bloqueado} data-testid="wa-send-btn" aria-label="Enviar"
                className={`h-10 w-10 shrink-0 rounded-full disabled:opacity-40 text-white flex items-center justify-center shadow ${modoNota ? 'bg-amber-500 hover:bg-amber-600' : 'bg-emerald-600 hover:bg-emerald-700'}`}>
                {modoNota ? <StickyNote className="w-4 h-4" /> : <Send className="w-4 h-4" />}
              </button>
              )}
            </footer>
          </>
        )}
        {selected && !selected.is_grupo && painelAberto && !largo && (
          <PainelCliente conv={selected} overlay setor={setoresMap[selected.setor_id]} recarregarEm={painelTick}
            onClose={alternarPainel} onPedirCadastro={pedirCadastro} ia={ia} onEtiquetas={salvarEtiquetas} />
        )}
      </section>
      {selected && !selected.is_grupo && painelAberto && largo && (
        <PainelCliente conv={selected} setor={setoresMap[selected.setor_id]} recarregarEm={painelTick}
          onClose={alternarPainel} onPedirCadastro={pedirCadastro} ia={ia} onEtiquetas={salvarEtiquetas} />
      )}

      {anexos.length > 0 && selected && (
        <AnexosModal arquivos={anexos} destino={displayName(selected)} onClose={() => setAnexos([])}
          onMais={escolherArquivos} onRemover={(i) => setAnexos(a => a.filter((_, j) => j !== i))} onEnviar={enviarAnexos} />
      )}

      {showFinalizar && selected && (
        <FinalizarModal conv={selected} padraoAvaliacao={cfgAtend?.pesquisa_satisfacao !== false} loading={acao === 'finalizar'}
          onClose={() => setShowFinalizar(false)} onConfirm={confirmarFinalizar} />
      )}

      {showTransfer && selected && (
        <TransferModal onClose={() => setShowTransfer(false)} onConfirm={transferir} atualId={selected.atendente_id}
          loading={acao === 'transferir'} setores={setores} abaInicial={showTransfer} setorAtual={selected.setor_id} />
      )}

      {showNova && (
        <NovaMensagemModal
          onClose={() => setShowNova(false)}
          onSent={async (jid) => {
            setShowNova(false);
            await loadConversations();
            const conv = convsRef.current.find(c => c.jid === jid);
            if (conv) setSelected(conv);
          }}
        />
      )}
    </div>
  );
}

/* ============================== componentes ============================== */

function Avatar({ name, size = 48, anel }) {
  return (
    <div className="rounded-full text-white flex items-center justify-center font-semibold shrink-0"
      style={{ width: size, height: size, fontSize: size * 0.32, background: colorFor(name, AVATAR_COLORS),
        boxShadow: anel ? `0 0 0 2px var(--wa-anel-fundo, #fff), 0 0 0 4px ${anel}` : undefined }}>
      {initials(name)}
    </div>
  );
}

const ConversationItem = memo(function ConversationItem({ c, active, onSelect, onDelete, userId, setor, etiquetasMap }) {
  const ets = (c.etiquetas || []).map(id => etiquetasMap[id]).filter(Boolean);
  const nome = displayName(c);
  const nl = c.nao_lidas || 0;
  const tid = c.numero || c.jid;
  return (
    <div onClick={onSelect} role="button" tabIndex={0} data-testid={`wa-conv-${tid}`}
      onKeyDown={e => e.key === 'Enter' && onSelect()}
      className={`group flex items-center gap-3 px-4 py-2.5 cursor-pointer transition-colors ${active ? 'bg-slate-100 dark:bg-slate-800 shadow-[inset_3px_0_0_#059669]' : 'hover:bg-slate-50 dark:hover:bg-slate-800/60'}`}>
      <Avatar name={nome} anel={anelEstado(c, userId)} />
      <div className="min-w-0 flex-1">
        <div className="flex justify-between items-baseline gap-2">
          <p className={`text-[15px] truncate ${nl ? 'font-semibold' : 'font-medium'}`}>{nome}</p>
          <span className={`text-xs shrink-0 ${nl ? 'text-emerald-600 font-semibold' : 'text-slate-400'}`}>{fmtListTime(c.ultima_data)}</span>
        </div>
        <div className="flex items-center gap-1 mt-0.5">
          <p className={`text-[13px] truncate flex-1 ${nl ? 'text-slate-800 dark:text-slate-200' : 'text-slate-500'}`}>{semMarcas(c.ultima_mensagem)}</p>
          {nl > 0 && <span className="bg-emerald-500 text-white text-[11px] font-semibold rounded-full min-w-[20px] h-5 px-1.5 flex items-center justify-center shrink-0">{nl}</span>}
        </div>
        <div className="flex items-center gap-1 mt-1 h-5 min-w-0 overflow-hidden">
          <StatusChip conv={c} userId={userId} small />
          {['irritado', 'negativo'].includes(c.ia_analise?.sentimento) && ['bot', 'fila', 'em_atendimento'].includes(c.atendimento_status) && (
            <span title={`Cliente ${c.ia_analise.sentimento}${c.ia_analise.urgencia === 'alta' ? ', urgência alta' : ''} (IA)`} data-testid={`wa-humor-${tid}`}
              className={`inline-flex items-center gap-0.5 text-[10.5px] font-medium shrink-0 ${c.ia_analise.sentimento === 'irritado' ? 'text-red-600 dark:text-red-400' : 'text-amber-700 dark:text-amber-400'}`}>
              {c.ia_analise.sentimento === 'irritado' ? <Angry className="w-3.5 h-3.5" /> : <Frown className="w-3.5 h-3.5" />}{c.ia_analise.sentimento}
            </span>
          )}
          {setor && <Chip cor={setor.cor} small>{setor.nome}</Chip>}
          {ets.slice(0, 2).map(e => <Chip key={e.id} cor={e.cor} small title={e.nome}>{e.nome}</Chip>)}
          {ets.length > 2 && <span className="text-[10.5px] text-slate-500 shrink-0" title={ets.slice(2).map(e => e.nome).join(', ')}>+{ets.length - 2}</span>}
          {c.cliente_id && !ets.length && <span className="text-[10px] text-emerald-700 bg-emerald-50 dark:bg-emerald-900/40 dark:text-emerald-300 rounded px-1.5 shrink-0">cliente</span>}
          <span className="flex-1" />
          <button onClick={e => { e.stopPropagation(); onDelete(); }} title="Excluir conversa" data-testid={`wa-conv-del-${tid}`}
            className="hidden group-hover:block text-slate-400 hover:text-red-600 p-0.5 rounded shrink-0">
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
});

/* ---------- atendimento: status, botões, transferência, cadastro ---------- */

function StatusChip({ conv, userId, small }) {
  const st = conv?.atendimento_status || 'bot';
  const base = `inline-flex items-center gap-1 rounded-full font-medium shrink-0 ${small ? 'text-[10.5px] px-1.5 py-px' : 'text-[11px] px-2 py-0.5'}`;
  if (conv?.is_grupo) return <span className={`${base} bg-teal-100 text-teal-800 dark:bg-teal-900/40 dark:text-teal-200`}><Users className="w-3 h-3" />Grupo</span>;
  if (conv?.cadastro_pendente_id) {
    return <span className={`${base} bg-violet-100 text-violet-800`}><ClipboardList className="w-3 h-3" />Cadastro p/ validar</span>;
  }
  if (st === 'fila') return <span className={`${base} bg-amber-100 text-amber-800`}><Clock className="w-3 h-3" />Na fila · {fmtEspera(conv.fila_desde)}</span>;
  if (st === 'em_atendimento') {
    const meu = conv.atendente_id && conv.atendente_id === userId;
    const sit = conv.situacao && conv.situacao !== 'em_atendimento' ? situacaoInfo(conv.situacao) : null;
    if (sit && small) return <span className={`${base} ${sit.cls}`} title={meu ? 'Com você' : conv.atendente_nome}>{sit.label}{meu ? '' : ` · ${(conv.atendente_nome || '').split(' ')[0]}`}</span>;
    return <span className={`${base} ${meu ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200' : 'bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200'}`}>
      <UserCheck className="w-3 h-3" />{meu ? 'Com você' : (conv.atendente_nome || 'Em atendimento')}</span>;
  }
  if (st === 'avaliacao') return <span className={`${base} bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300`}>⭐ Aguardando avaliação</span>;
  if (st === 'encerrada') {
    return conv.encerramento === 'finalizado'
      ? <span className={`${base} bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400`}><CheckCircle2 className="w-3 h-3" />Finalizada</span>
      : <span className={`${base} bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400`}><Archive className="w-3 h-3" />Arquivada</span>;
  }
  return <span className={`${base} bg-violet-100 text-violet-800 dark:bg-violet-900/40 dark:text-violet-200`}><Bot className="w-3 h-3" />Triagem</span>;
}

function HeaderBtn({ onClick, loading, icon: Icon, children, primary, testid, compacto }) {
  return (
    <button onClick={onClick} disabled={loading} data-testid={testid} title={typeof children === 'string' ? children : undefined}
      className={`hidden sm:flex h-9 px-2.5 xl:px-3 rounded-lg text-sm font-medium items-center gap-1.5 shrink-0 disabled:opacity-60 transition-colors ${primary ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : 'border border-slate-200 dark:border-slate-700 bg-white hover:bg-slate-50 text-slate-700 dark:bg-slate-900 dark:hover:bg-slate-800 dark:text-slate-200'}`}>
      {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Icon className="w-4 h-4" />}<span className={primary ? '' : compacto ? 'sr-only' : 'hidden xl:inline'}>{children}</span>
    </button>
  );
}

function MenuItem({ icon: Icon, children, onClick, danger }) {
  return (
    <button onClick={onClick} className={`w-full text-left px-3 py-2 flex items-center gap-2 hover:bg-slate-50 dark:hover:bg-slate-800 ${danger ? 'text-red-600' : ''}`}>
      <Icon className="w-4 h-4" />{children}
    </button>
  );
}

function FinalizarModal({ conv, padraoAvaliacao, loading, onClose, onConfirm }) {
  const [avaliacao, setAvaliacao] = useState(padraoAvaliacao);
  const st = conv.atendimento_status;
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white dark:bg-slate-900 rounded-2xl w-full max-w-sm shadow-xl overflow-hidden" onClick={e => e.stopPropagation()}
        role="dialog" aria-labelledby="wa-fin-titulo" data-testid="wa-finalizar-modal">
        <div className="px-5 py-4 border-b dark:border-slate-800 flex items-center justify-between">
          <h3 id="wa-fin-titulo" className="font-semibold flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600" /> Finalizar atendimento</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600" aria-label="Fechar"><X className="w-5 h-5" /></button>
        </div>
        <div className="px-5 py-4 space-y-3 text-sm">
          <p className="text-slate-600 dark:text-slate-300">
            {st === 'fila' ? 'A conversa está na fila; ' : st === 'bot' ? 'A conversa está na triagem; ' : ''}
            o cliente recebe a mensagem de encerramento e a conversa sai da lista. O histórico fica na ficha
            {conv.cliente_id ? ' e no cadastro do cliente' : ''}.
          </p>
          <label className="flex items-start gap-3 rounded-lg border border-slate-200 dark:border-slate-700 p-3 cursor-pointer">
            <input type="checkbox" checked={avaliacao} onChange={e => setAvaliacao(e.target.checked)} className="mt-0.5 h-4 w-4 accent-emerald-600"
              data-testid="wa-fin-avaliacao" />
            <span>
              <span className="font-medium block">Pedir avaliação ao cliente</span>
              <span className="text-xs text-slate-500">Nota de 1 a 5. O padrão vem de Configurações → Assistente.</span>
            </span>
          </label>
        </div>
        <div className="px-5 py-3 border-t dark:border-slate-800 flex justify-end gap-2">
          <button onClick={onClose} className="h-9 px-4 rounded-lg border text-sm">Cancelar</button>
          <button onClick={() => onConfirm(avaliacao)} disabled={loading} data-testid="wa-fin-confirmar"
            className="h-9 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium disabled:opacity-50 flex items-center gap-1.5">
            {loading && <Loader2 className="w-4 h-4 animate-spin" />} Finalizar
          </button>
        </div>
      </div>
    </div>
  );
}

function TransferModal({ onClose, onConfirm, atualId, loading, setores = [], abaInicial, setorAtual }) {
  const [lista, setLista] = useState(null);
  const [para, setPara] = useState(null);
  const [nota, setNota] = useState('');
  const temSetores = setores.some(s => s.ativo !== false);
  const [modo, setModo] = useState(abaInicial === 'setor' && temSetores ? 'setor' : 'atendente');
  const [setorId, setSetorId] = useState(null);
  const ativos = setores.filter(s => s.ativo !== false);
  const setoresDe = (uid) => ativos.filter(s => (s.membros || []).includes(uid));
  useEffect(() => { whatsappAPI.atendentes().then(r => setLista(r.data || [])).catch(() => setLista([])); }, []);
  const dot = { online: 'bg-emerald-500', ausente: 'bg-amber-400', ocupado: 'bg-red-500' };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white dark:bg-slate-900 rounded-2xl w-full max-w-md shadow-xl overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="px-5 py-4 border-b flex items-center justify-between">
          <h3 className="font-semibold flex items-center gap-2"><ArrowRightLeft className="w-4 h-4" /> Transferir atendimento</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
        </div>
        {temSetores && (
          <div className="px-5 pt-3">
            <div className="flex gap-1 p-1 rounded-lg bg-slate-100 dark:bg-slate-800 text-sm">
              {[['atendente', 'Para um atendente'], ['setor', 'Para a fila de um setor']].map(([k, l]) => (
                <button key={k} onClick={() => { setModo(k); setPara(null); setSetorId(null); }} data-testid={`wa-transf-modo-${k}`}
                  className={`flex-1 h-8 rounded-md font-medium ${modo === k ? 'bg-white dark:bg-slate-900 shadow-sm' : 'text-slate-500'}`}>{l}</button>
              ))}
            </div>
          </div>
        )}
        {modo === 'setor' ? (
          <div className="max-h-72 overflow-y-auto py-2">
            {ativos.map(s => (
              <button key={s.id} onClick={() => setSetorId(s.id)} data-testid={`wa-transf-setor-${s.id}`}
                className={`w-full px-5 py-2.5 flex items-center gap-3 text-left ${setorId === s.id ? 'bg-emerald-50 dark:bg-emerald-900/30' : 'hover:bg-slate-50 dark:hover:bg-slate-800'}`}>
                <span className="w-8 h-8 rounded-lg text-white text-sm font-semibold flex items-center justify-center shrink-0" style={{ background: s.cor }}>{s.nome[0]}</span>
                <span className="flex-1 text-sm font-medium">{s.nome}</span>
                {s.id === setorAtual && <span className="text-xs text-slate-400">setor atual</span>}
              </button>
            ))}
            <p className="px-5 pt-2 text-xs text-slate-500">A conversa volta para a fila desse setor e o cliente é avisado.</p>
          </div>
        ) : (
        <div className="max-h-72 overflow-y-auto py-1">
          {!lista && <p className="p-4 text-sm text-slate-500">Carregando…</p>}
          {lista?.filter(u => u.id !== atualId).map(u => (
            <button key={u.id} onClick={() => setPara(u.id)} data-testid={`wa-transf-${u.id}`}
              className={`w-full px-5 py-2.5 flex items-center gap-3 text-left ${para === u.id ? 'bg-emerald-50 dark:bg-emerald-900/30' : 'hover:bg-slate-50 dark:hover:bg-slate-800'}`}>
              <span className="relative"><Avatar name={u.nome} size={34} />
                <span className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-white ${dot[u.presenca] || 'bg-slate-300'}`} /></span>
              <span className="flex-1 min-w-0">
                <span className="text-sm font-medium block">{u.nome}</span>
                {setoresDe(u.id).length > 0 && (
                  <span className="flex gap-1 mt-0.5 flex-wrap">{setoresDe(u.id).map(s => <Chip key={s.id} cor={s.cor} small>{s.nome}</Chip>)}</span>
                )}
              </span>
              <span className="text-xs text-slate-400">{u.presenca === 'online' ? 'online' : u.presenca}</span>
            </button>
          ))}
        </div>
        )}
        <div className="px-5 py-3 border-t space-y-3">
          <input value={nota} onChange={e => setNota(e.target.value)} placeholder="Recado para o colega (opcional, o cliente não vê)"
            className="w-full h-9 px-3 rounded-lg bg-slate-100 dark:bg-slate-800 text-sm outline-none" />
          <div className="flex justify-end gap-2">
            <button onClick={onClose} className="h-9 px-4 rounded-lg border text-sm">Cancelar</button>
            <button onClick={() => (modo === 'setor' ? onConfirm(null, nota || null, setorId) : onConfirm(para, nota || null, (setoresDe(para).length === 1 ? setoresDe(para)[0].id : null)))}
              disabled={(modo === 'setor' ? !setorId : !para) || loading} data-testid="wa-transf-confirmar"
              className="h-9 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium disabled:opacity-50 flex items-center gap-1.5">
              {loading && <Loader2 className="w-4 h-4 animate-spin" />} Transferir
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function CadastroCard({ cadastroId, onDone }) {
  const [cad, setCad] = useState(null);
  const [form, setForm] = useState(null);
  const [aberto, setAberto] = useState(true);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    whatsappAPI.cadastros('pendente').then(r => {
      const c = (r.data || []).find(x => x.id === cadastroId);
      setCad(c || null);
      if (c) setForm({ ...c.dados, telefone: c.telefone });
    }).catch(() => setCad(null));
  }, [cadastroId]);
  if (!cad || !form) return null;
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));
  const aprovar = async () => {
    setBusy(true);
    try { await whatsappAPI.aprovarCadastro(cad.id, form); toast.success('Cadastro aprovado — cliente criado na base'); onDone && onDone(); }
    catch (e) { toast.error(errMsg(e, 'Não foi possível aprovar')); }
    finally { setBusy(false); }
  };
  const recusar = async () => {
    const motivo = window.prompt('Motivo da recusa (o cliente vai receber esta mensagem):');
    if (!motivo) return;
    setBusy(true);
    try { await whatsappAPI.rejeitarCadastro(cad.id, motivo); toast.success('Cadastro recusado'); onDone && onDone(); }
    catch (e) { toast.error(errMsg(e, 'Não foi possível recusar')); }
    finally { setBusy(false); }
  };
  const campo = (k, label, cls = '') => (
    <label className={`text-[11px] text-violet-900/70 font-medium ${cls}`}>{label}
      <input value={form[k] || ''} onChange={set(k)} className="mt-0.5 w-full h-8 px-2 rounded-md border border-violet-200 bg-white text-sm text-slate-900 outline-none focus:ring-2 focus:ring-violet-300" />
    </label>
  );
  return (
    <div className="border-b bg-violet-50 dark:bg-violet-950/30 px-4 py-3" data-testid="wa-cadastro-card">
      <button onClick={() => setAberto(v => !v)} className="w-full flex items-center gap-2 text-sm font-semibold text-violet-900 dark:text-violet-200">
        <ClipboardList className="w-4 h-4" /> Cadastro enviado pelo cliente — revise e aprove
        <ChevronDown className={`w-4 h-4 ml-auto transition-transform ${aberto ? 'rotate-180' : ''}`} />
      </button>
      {aberto && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-6 gap-2 mt-2">
            {campo('nome_completo', 'Nome completo', 'col-span-2 lg:col-span-2')}
            {campo('cpf', 'CPF')}
            {campo('data_nascimento', 'Nascimento (aaaa-mm-dd)')}
            {campo('cidade', 'Cidade')}
            {campo('estado', 'UF')}
          </div>
          <div className="flex items-center gap-2 mt-2">
            <span className="text-xs text-violet-900/60">Telefone: {form.telefone}</span>
            <span className="flex-1" />
            <button onClick={recusar} disabled={busy} className="h-8 px-3 rounded-lg border border-violet-300 text-sm text-violet-900 hover:bg-violet-100 disabled:opacity-50">Recusar</button>
            <button onClick={aprovar} disabled={busy} data-testid="wa-cadastro-aprovar"
              className="h-8 px-3 rounded-lg bg-violet-600 hover:bg-violet-700 text-white text-sm font-medium flex items-center gap-1.5 disabled:opacity-50">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Aprovar e criar cliente
            </button>
          </div>
        </>
      )}
    </div>
  );
}

/* ---------- filtros da lista e barra de organização da conversa ---------- */

const selectCls = 'w-full h-8 px-2 rounded-lg bg-slate-100 dark:bg-slate-800 text-sm outline-none border-0';

const ETAPAS = [['sem_triagem', 'Sem a triagem'], ['triagem', 'Só triagem (com o assistente)'], ['fila', 'Só na fila'], ['em_atendimento', 'Só em atendimento']];
function FiltrosBar({ filtros, setFiltros, aberto, setAberto, setores, etiquetas, setoresMap, etiquetasMap, filaSetores, comEtapa }) {
  const set = (k) => (e) => setFiltros(f => ({ ...f, [k]: e.target.value }));
  const limpar = (k) => setFiltros(f => ({ ...f, [k]: '' }));
  const n = ['setor', 'etiqueta', 'situacao', ...(comEtapa ? ['etapa'] : [])].filter(k => filtros[k]).length;
  const nomeSetor = filtros.setor === 'sem' ? 'Sem setor' : setoresMap[filtros.setor]?.nome;
  return (
    <div className="space-y-2" data-testid="wa-filtros">
      <div className="flex items-center gap-1.5 flex-wrap">
        <button onClick={() => setAberto(v => !v)} data-testid="wa-filtros-toggle"
          className={`h-7 px-2.5 rounded-lg text-xs font-medium flex items-center gap-1.5 border ${aberto || n ? 'border-emerald-300 text-emerald-700 bg-emerald-50 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-800' : 'text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800'}`}>
          <SlidersHorizontal className="w-3.5 h-3.5" /> Filtros{n > 0 && <span className="rounded-full bg-emerald-600 text-white px-1.5 text-[10px]">{n}</span>}
        </button>
        {filtros.setor && <Chip cor={setoresMap[filtros.setor]?.cor || '#64748b'} onRemove={() => limpar('setor')}>{nomeSetor || 'setor'}</Chip>}
        {filtros.etiqueta && <Chip cor={etiquetasMap[filtros.etiqueta]?.cor} onRemove={() => limpar('etiqueta')}>{etiquetasMap[filtros.etiqueta]?.nome || 'etiqueta'}</Chip>}
        {filtros.situacao && <Chip cor="#0284c7" onRemove={() => limpar('situacao')}>{situacaoInfo(filtros.situacao)?.label}</Chip>}
        {comEtapa && filtros.etapa && <Chip cor="#8b5cf6" onRemove={() => limpar('etapa')}>{(ETAPAS.find(([k]) => k === filtros.etapa) || [])[1]}</Chip>}
      </div>
      {aberto && (
        <div className="grid grid-cols-1 gap-1.5 p-2 rounded-xl border bg-white dark:bg-slate-900">
          {comEtapa && (
            <select value={filtros.etapa || ''} onChange={set('etapa')} className={selectCls} data-testid="wa-filtro-etapa">
              <option value="">Todas as etapas (com triagem)</option>
              {ETAPAS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
          )}
          {setores.length > 0 && (
            <select value={filtros.setor} onChange={set('setor')} className={selectCls} data-testid="wa-filtro-setor">
              <option value="">Todos os setores</option>
              {setores.map(x => <option key={x.id} value={x.id}>{x.nome}{filaSetores?.[x.id] ? ` (${filaSetores[x.id]} na fila)` : ''}</option>)}
              <option value="sem">Sem setor{filaSetores?.sem ? ` (${filaSetores.sem} na fila)` : ''}</option>
            </select>
          )}
          {etiquetas.length > 0 && (
            <select value={filtros.etiqueta} onChange={set('etiqueta')} className={selectCls} data-testid="wa-filtro-etiqueta">
              <option value="">Todas as etiquetas</option>
              {etiquetas.map(x => <option key={x.id} value={x.id}>{x.nome}</option>)}
            </select>
          )}
          <select value={filtros.situacao} onChange={set('situacao')} className={selectCls} data-testid="wa-filtro-situacao">
            <option value="">Qualquer situação</option>
            {SITUACOES.map(x => <option key={x.id} value={x.id}>{x.label}</option>)}
          </select>
        </div>
      )}
    </div>
  );
}

function OrganizacaoBar({ conv, setor, etiquetas, etiquetasMap, podeSituacao, onSituacao, carregando, onEtiquetas, onSetor }) {
  const [aberto, setAberto] = useState(false);
  const [paraEsquerda, setParaEsquerda] = useState(false);
  const ref = useRef(null);
  const abrir = () => {
    const r = ref.current?.getBoundingClientRect();
    setParaEsquerda(!!r && r.left + 240 > window.innerWidth - 12);   // não deixa o menu sair da tela
    setAberto(v => !v);
  };
  useEffect(() => {
    if (!aberto) return undefined;
    const fora = (e) => { if (ref.current && !ref.current.contains(e.target)) setAberto(false); };
    document.addEventListener('mousedown', fora);
    return () => document.removeEventListener('mousedown', fora);
  }, [aberto]);
  const ids = conv.etiquetas || [];
  const ets = ids.map(id => etiquetasMap[id]).filter(Boolean);
  const st = conv.atendimento_status || 'bot';
  const sit = situacaoInfo(conv.situacao);
  const toggle = (id) => onEtiquetas(ids.includes(id) ? ids.filter(x => x !== id) : [...ids, id]);
  return (
    <div className="px-4 py-1.5 border-b bg-white dark:bg-slate-900 flex items-center gap-2 flex-wrap text-xs min-h-[40px]" data-testid="wa-org-bar">
      {(setor || onSetor) && (
        <button onClick={onSetor || undefined} disabled={!onSetor} title={onSetor ? 'Encaminhar para outro setor' : undefined}
          className="flex items-center gap-1 text-slate-500 enabled:hover:text-slate-800 dark:enabled:hover:text-slate-200" data-testid="wa-org-setor">
          <Layers className="w-3.5 h-3.5" />
          {setor ? <Chip cor={setor.cor}>{setor.nome}</Chip> : <span className="italic">sem setor</span>}
        </button>
      )}
      {st === 'em_atendimento' && (
        podeSituacao ? (
          <span className="flex items-center gap-1">
            <span className={`w-2 h-2 rounded-full ${sit?.dot || 'bg-emerald-500'}`} />
            <select value={conv.situacao || 'em_atendimento'} onChange={e => onSituacao(e.target.value)} disabled={carregando} data-testid="wa-situacao"
              className="h-7 pl-1 pr-6 rounded-md bg-transparent hover:bg-slate-100 dark:hover:bg-slate-800 text-xs font-medium outline-none cursor-pointer disabled:opacity-50">
              {SITUACOES.map(x => <option key={x.id} value={x.id}>{x.label}</option>)}
            </select>
            {carregando && <Loader2 className="w-3 h-3 animate-spin" />}
          </span>
        ) : sit && sit.id !== 'em_atendimento' && <span className={`rounded-full px-2 py-0.5 font-medium ${sit.cls}`}>{sit.label}</span>
      )}
      <span className="w-px h-4 bg-slate-200 dark:bg-slate-700" />
      {ets.map(e => <Chip key={e.id} cor={e.cor} onRemove={() => toggle(e.id)}>{e.nome}</Chip>)}
      <div className="relative" ref={ref}>
        <button onClick={abrir} data-testid="wa-etiquetas-btn"
          className="h-6 px-2 rounded-full border border-dashed text-slate-500 hover:text-slate-800 hover:border-slate-400 dark:hover:text-slate-200 flex items-center gap-1">
          {ets.length ? <Plus className="w-3 h-3" /> : <Tag className="w-3 h-3" />}{ets.length ? '' : 'Etiqueta'}
        </button>
        {aberto && (
          <div className={`absolute ${paraEsquerda ? 'right-0' : 'left-0'} top-8 z-30 w-56 max-h-72 overflow-y-auto rounded-xl border bg-white dark:bg-slate-900 shadow-lg py-1`} data-testid="wa-etiquetas-menu">
            {etiquetas.map(e => (
              <label key={e.id} className="flex items-center gap-2 px-3 py-1.5 hover:bg-slate-50 dark:hover:bg-slate-800 cursor-pointer text-sm">
                <input type="checkbox" checked={ids.includes(e.id)} onChange={() => toggle(e.id)} className="w-4 h-4 accent-emerald-600" />
                <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: e.cor }} />
                <span className="truncate">{e.nome}</span>
              </label>
            ))}
            {!etiquetas.length && <p className="px-3 py-2 text-xs text-slate-500">Nenhuma etiqueta. Crie em Configurações → Etiquetas.</p>}
          </div>
        )}
      </div>
    </div>
  );
}

function comMencoes(texto, mencoes) {
  const nomes = mencoes.map(m => `@${m.nome}`).sort((a, b) => b.length - a.length);
  const escapar = (n) => n.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const rx = new RegExp('(' + nomes.map(escapar).join('|') + ')', 'g');
  return texto.split(rx).map((p, i) => (nomes.includes(p)
    ? <span key={i} className="font-semibold text-amber-900 bg-amber-200/80 dark:bg-amber-500/30 dark:text-amber-100 rounded px-0.5">{p}</span>
    : <React.Fragment key={i}>{formatWa(p)}</React.Fragment>));
}

const MessageBubble = memo(function MessageBubble({ m, first, onRetry, destaque }) {
  if (m.message_type === 'sistema') {     // eventos do atendimento: linha do tempo
    return (
      <div className="flex items-center gap-3 my-2.5 px-2" data-testid={`wa-msg-${m.id}`}>
        <span className="h-px flex-1 bg-slate-300/70 dark:bg-slate-700" />
        <span className="text-[11.5px] text-slate-500 dark:text-slate-400 text-center max-w-[75%]">
          {m.text} <span className="text-slate-400 dark:text-slate-500">{new Date(m.timestamp).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}</span>
        </span>
        <span className="h-px flex-1 bg-slate-300/70 dark:bg-slate-700" />
      </div>
    );
  }
  if (m.message_type === 'nota') {
    return (
      <div className="flex justify-end mt-2" data-testid={`wa-msg-${m.id}`}>
        <div className={`max-w-[70%] rounded-lg px-3 py-2 bg-amber-100 border border-amber-300 text-amber-950 dark:bg-amber-950/60 dark:border-amber-800 dark:text-amber-100 shadow-sm ${destaque ? 'ring-4 ring-amber-400/60' : ''}`}>
          <p className="text-[11.5px] font-semibold flex items-center gap-1 mb-0.5"><Lock className="w-3 h-3" /> Nota interna de {m.sent_by_name}</p>
          <p className="text-[14px] whitespace-pre-wrap break-words">{m.mencoes?.length ? comMencoes(m.text, m.mencoes) : formatWa(m.text)}</p>
          <p className="text-[10.5px] text-amber-700 dark:text-amber-300/80 text-right">{new Date(m.timestamp).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}</p>
        </div>
      </div>
    );
  }
  // texto automático da mídia ("[imagem]", "[documento] nome.pdf"): o cartão do anexo já mostra
  const hideText = (['image', 'video', 'audio', 'sticker'].includes(m.message_type)
    && ['[imagem]', '[vídeo]', '[áudio]', '[sticker]'].includes(m.text))
    || (m.message_type === 'document' && (m.media_file || m.media_pending || m._localUrl) && /^\[documento\]/.test(m.text || ''));
  const time = new Date(m.timestamp).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  return (
    <div className={`flex ${m.from_me ? 'justify-end' : 'justify-start'} ${first ? 'mt-3' : 'mt-0.5'}`}>
      <div data-testid={`wa-msg-${m.id}`}
        className={`relative max-w-[70%] rounded-xl px-2.5 pt-1.5 pb-1 shadow-[0_1px_0.5px_rgba(0,0,0,.13)] text-slate-900 dark:text-slate-100 transition-shadow duration-700
          ${m.from_me ? 'bg-[#dcf5e1] dark:bg-emerald-900/70' : 'bg-white dark:bg-slate-800'} ${first ? (m.from_me ? 'rounded-tr-sm' : 'rounded-tl-sm') : ''}
          ${m._temp && !m._failed ? 'opacity-80' : ''} ${destaque ? 'ring-4 ring-amber-400/70' : ''}`}>
        {first && m.from_me && m.sent_by_name && (
          m.bot
            ? <p className="text-[12.5px] font-semibold mb-0.5 text-slate-500 dark:text-slate-400 flex items-center gap-1"><Bot className="w-3.5 h-3.5" /> {m.sent_by_name}</p>
            : <p className="text-[12.5px] font-semibold mb-0.5" style={{ color: colorFor(m.sent_by_name, SENDER_COLORS) }}>{m.sent_by_name}</p>
        )}
        {first && !m.from_me && m.participante_nome && (
          <p className="text-[12.5px] font-semibold mb-0.5" style={{ color: colorFor(m.participante_numero || m.participante_nome, SENDER_COLORS) }}>{m.participante_nome}</p>
        )}
        <MediaRender msg={m} />
        {m.text && !hideText && (
          <p className="text-[14.2px] leading-[19px] whitespace-pre-wrap break-words">
            {formatWa(m.text)}<span className="inline-block w-16" />
          </p>
        )}
        <div className={`flex items-center gap-1 justify-end text-[11px] text-slate-500 dark:text-slate-400 h-4 ${m.text && !hideText ? '-mt-4' : 'mt-1'}`}>
          <span>{time}</span>
          {m.from_me && <MsgStatus status={m.status_code} failed={m._failed} />}
        </div>
        {m._failed && (
          <button onClick={() => onRetry(m)} className="mt-1 text-xs text-red-600 font-medium flex items-center gap-1">
            <RotateCw className="w-3 h-3" /> Não enviada · tentar de novo
          </button>
        )}
      </div>
    </div>
  );
});

function MsgStatus({ status, failed }) {
  // Baileys: 1=ERROR, 2=PENDING, 3=SERVER_ACK, 4=DELIVERED, 5=READ (1 também usado aqui para "enviando")
  if (failed) return <AlertCircle className="w-3.5 h-3.5 text-red-500" />;
  if (!status || status <= 2) return <Clock className="w-3 h-3 opacity-60" />;
  if (status === 3) return <Check className="w-3.5 h-3.5" />;
  if (status === 4) return <CheckCheck className="w-3.5 h-3.5" />;
  return <CheckCheck className="w-3.5 h-3.5 text-sky-500" />;
}

function ConnectionPill({ status }) {
  const map = {
    connected: ['bg-emerald-500', 'text-emerald-700', 'Conectado'],
    connecting: ['bg-amber-500', 'text-amber-700', 'Conectando…'],
    reconnecting: ['bg-amber-500', 'text-amber-700', 'Reconectando…'],
    qr: ['bg-amber-500', 'text-amber-700', 'Aguardando QR Code'],
    conflict: ['bg-red-500', 'text-red-700', 'Sessão aberta em outro lugar'],
    logged_out: ['bg-red-500', 'text-red-700', 'Desconectado'],
    service_offline: ['bg-red-500', 'text-red-700', 'Serviço offline'],
  };
  const [dot, txt, label] = map[status] || ['bg-slate-400', 'text-slate-500', status ? 'Offline' : '…'];
  return (
    <div className={`flex items-center gap-1.5 text-xs font-medium mt-0.5 ${txt}`}>
      <span className={`h-2 w-2 rounded-full ${dot} ${status === 'connected' ? '' : 'animate-pulse'}`} /> {label}
    </div>
  );
}

function ConnectionBanner({ status }) {
  const recon = ['connecting', 'reconnecting'].includes(status);
  return (
    <div className={`px-4 py-2 text-sm flex items-center gap-2 border-b ${recon ? 'bg-amber-50 border-amber-200 text-amber-900' : 'bg-red-50 border-red-200 text-red-900'}`}>
      {recon
        ? <><span className="h-4 w-4 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" /><b>Reconectando ao WhatsApp…</b><span className="opacity-80">isso costuma levar alguns segundos.</span></>
        : <><AlertCircle className="w-4 h-4" /><b>WhatsApp desconectado.</b><Link to="/whatsapp/conexao" className="underline">Abrir tela de conexão</Link></>}
    </div>
  );
}

function MessagesSkeleton() {
  return (
    <div className="space-y-3 animate-pulse">
      {[55, 40, 65, 35, 50].map((w, i) => (
        <div key={i} className={`flex ${i % 2 ? 'justify-end' : ''}`}><div className="h-9 rounded-lg bg-white/70" style={{ width: `${w}%` }} /></div>
      ))}
    </div>
  );
}

function MediaRender({ msg }) {
  const [falhou, setFalhou] = useState(false);
  if (msg._localUrl) {   // ainda subindo: mostra o arquivo local com o andamento
    const p = msg._progresso ?? 0;
    const andamento = msg._failed ? null : (
      <div className="mt-1" data-testid={`wa-upload-${msg.id}`}>
        <div className="h-1 rounded bg-black/10 overflow-hidden"><div className="h-full bg-emerald-600 transition-all" style={{ width: `${p}%` }} /></div>
        <p className="text-[11px] text-slate-500 mt-0.5">{p < 100 ? `Enviando ${p}%` : 'Enviando ao WhatsApp…'}</p>
      </div>
    );
    if (msg.message_type === 'image') {
      return <div className="mb-1 w-fit max-w-full"><img src={msg._localUrl} alt="" className="rounded max-w-full max-h-80 object-cover opacity-80" />{andamento}</div>;
    }
    if (msg.message_type === 'video') {
      return <div className="mb-1 w-fit max-w-full"><video src={msg._localUrl} muted className="rounded max-w-full max-h-80 opacity-80" />{andamento}</div>;
    }
    if (msg.message_type === 'audio') {
      return (
        <div className="mb-1 w-64 max-w-full">
          <div className="flex items-center gap-2">
            {msg.voz && <Mic className="w-4 h-4 text-emerald-700 shrink-0" />}
            <audio src={msg._localUrl} controls preload="metadata" className="w-full h-9" />
          </div>
          {andamento}
        </div>
      );
    }
    return (
      <div className="p-2.5 bg-black/5 rounded-lg mb-1.5 w-72 max-w-full">
        <p className="text-sm font-medium truncate flex items-center gap-2"><Paperclip className="w-4 h-4 shrink-0 text-slate-500" />{msg.media_filename}</p>
        {andamento}
      </div>
    );
  }
  if (msg.media_pending) {
    return <div className="w-56 h-32 rounded bg-black/5 flex items-center justify-center text-xs text-slate-500 mb-1 animate-pulse">Baixando mídia…</div>;
  }
  if (msg.media_error && !msg.media_file) {
    return <div className="text-xs text-slate-500 italic mb-1">Mídia indisponível</div>;
  }
  if (!msg.media_file) return null;
  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
  const token = localStorage.getItem('agrolink_token');
  const url = `${API}/whatsapp/media/${encodeURIComponent(msg.id)}?token=${encodeURIComponent(token || '')}`;
  const urlBaixar = `${url}&baixar=1`;
  const type = msg.message_type;
  const mime = msg.media_mime || '';
  const filename = msg.media_filename || 'arquivo';
  const size = msg.media_size ? (msg.media_size >= 1048576 ? `${(msg.media_size / 1048576).toFixed(1).replace('.', ',')} MB` : `${Math.max(1, Math.round(msg.media_size / 1024))} KB`) : '';
  const baixar = (
    <a href={urlBaixar} title="Baixar" aria-label={`Baixar ${filename}`} data-testid={`wa-baixar-${msg.id}`}
      className="h-8 w-8 rounded-full flex items-center justify-center text-slate-600 hover:bg-black/10 shrink-0">
      <Download className="w-4 h-4" />
    </a>
  );
  if (falhou) {
    return (
      <div className="flex items-center gap-2 text-xs text-slate-500 italic mb-1">
        Não foi possível mostrar este arquivo. {baixar}
      </div>
    );
  }

  if (type === 'image' || (type === 'sticker' && mime.startsWith('image/'))) {
    return (
      <div className="relative group mb-1 w-fit">
        <a href={url} target="_blank" rel="noopener noreferrer" className="block">
          <img src={url} alt="imagem" loading="lazy" onError={() => setFalhou(true)}
            className={`rounded ${type === 'sticker' ? 'w-32' : 'max-w-full max-h-80 object-cover'}`} />
        </a>
        <a href={urlBaixar} title="Baixar imagem" aria-label="Baixar imagem" data-testid={`wa-baixar-${msg.id}`}
          className="absolute top-1.5 right-1.5 h-8 w-8 rounded-full bg-black/55 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity">
          <Download className="w-4 h-4" />
        </a>
      </div>
    );
  }
  if (type === 'video' || type === 'audio') {
    return (
      <div className="flex items-center gap-1 mb-1">
        {type === 'audio' && (msg.voz || (msg.media_mime || '').includes('ogg')) && (
          <span className="h-8 w-8 rounded-full bg-emerald-600 text-white flex items-center justify-center shrink-0" title="Mensagem de voz"><Mic className="w-4 h-4" /></span>
        )}
        {type === 'video'
          ? <video src={url} controls preload="metadata" onError={() => setFalhou(true)} className="rounded max-w-full max-h-80" />
          : <audio src={url} controls preload="metadata" onError={() => setFalhou(true)} className="w-64 max-w-full" />}
        {baixar}
      </div>
    );
  }
  const ext = (filename.includes('.') ? filename.split('.').pop() : (mime.split('/')[1] || 'arq')).slice(0, 4).toUpperCase();
  return (
    <div className="flex items-center gap-1 p-2 bg-black/5 rounded-lg mb-1.5 w-72 max-w-full" data-testid={`wa-anexo-${msg.id}`}>
      <a href={url} target="_blank" rel="noopener noreferrer" title="Abrir" className="flex items-center gap-3 min-w-0 flex-1 no-underline text-slate-900 hover:opacity-80">
        <div className="w-10 h-10 rounded bg-red-500 text-white flex items-center justify-center text-[10px] font-bold shrink-0">{ext}</div>
        <div className="min-w-0">
          <p className="text-sm font-medium truncate">{filename}</p>
          <p className="text-xs text-slate-500">{size}{size && ' · '}{ext}</p>
        </div>
      </a>
      {baixar}
    </div>
  );
}
